// frontend/src/features/campaign/components/create/CampaignOutreachLauncherModal.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from '../../../../api/axios';
import { useAuth } from '../../../../contexts/KeycloakContext';
import './CampaignOutreachLauncherModal.css';

import {
  cleanPhoneNumber,
  interpolateTokens,
  buildIsolatedGmailComposeUrl,
  buildWhatsAppClickToChatUrl,
  buildLinkedInProfileUrl,
  truncateForLinkedInNote,
  LINKEDIN_NOTE_CHAR_LIMIT,
  LINKEDIN_INMAIL_SUBJECT_LIMIT,
  ensureTemplateTokens,
  DEFAULT_LINKEDIN_NOTE,
  DEFAULT_LINKEDIN_INMAIL_BODY,
  copyTextToClipboard,
  buildTelUri,
  buildQrCodeUrl,
  buildWhatsAppDirectChatUrl,
  buildWhatsAppDesktopUri,
  DEFAULT_PHONE_BATTLECARD,
  CALL_DISPOSITIONS,
} from '../../utils/outreachInterpolation';
import { computeStepLeadBreakdown } from '../../utils/campaignReachabilityUtils';
import {
  DEFAULT_TIMING_CONFIG,
  resolveProspectTimezone,
  resolveEffectiveTimezone,
  isInsideSendingWindow,
  calculateNextEligibleSendTime,
  formatLocalDateTimeInTz,
} from '../../utils/campaignTimingUtils';

export default function CampaignOutreachLauncherModal({
  isOpen,
  onClose,
  channel = 'Email',
  touchpoint,
  campaign,
  recipients = [],
  onComplete,
  onUpdateDispatchedLeads,
}) {
  const { keycloak } = useAuth();
  const [activeChannel, setActiveChannel] = useState(channel || 'Email');

  useEffect(() => {
    if (channel) {
      setActiveChannel(channel);
    }
  }, [channel]);

  const isPhone = /phone|call/i.test(activeChannel);
  const isWhatsApp = !isPhone && /whatsapp/i.test(activeChannel);
  const isLinkedIn = !isPhone && /linkedin/i.test(activeChannel);
  const normalizedChannel = isPhone
    ? 'Phone Call'
    : isLinkedIn
      ? 'LinkedIn'
      : isWhatsApp
        ? 'WhatsApp'
        : 'Email';

  // Phone Call State: Battlecard, Dialer, Live Timer & Dispositions
  const [phoneBattlecard, setPhoneBattlecard] = useState(DEFAULT_PHONE_BATTLECARD);
  const [activeObjectionKey, setActiveObjectionKey] = useState('competitor');
  const [callStatus, setCallStatus] = useState('idle'); // 'idle' | 'calling' | 'connected' | 'ended'
  const [callSeconds, setCallSeconds] = useState(0);
  const [callDisposition, setCallDisposition] = useState('');
  const [callNotes, setCallNotes] = useState('');
  const [showQrCode, setShowQrCode] = useState(false);
  const [qrCodeMode, setQrCodeMode] = useState('tel'); // 'tel' | 'whatsapp'
  const [whatsAppGuideActive, setWhatsAppGuideActive] = useState(false);

  // LinkedIn Mode: 'connection_note' (<=300 chars) | 'inmail' (Subject + Body)
  const [linkedInMode, setLinkedInMode] = useState('connection_note');
  const [connectionNote, setConnectionNote] = useState(DEFAULT_LINKEDIN_NOTE);
  const [copiedButtonId, setCopiedButtonId] = useState(null);

  // Studio Mode: 'edit' (Edit Template) | 'preview' (Live Preview)
  const [editorMode, setEditorMode] = useState('edit');

  // Input focus references for cursor position token insertion
  const lastFocusedInputRef = useRef('body');
  const subjectInputRef = useRef(null);
  const bodyTextareaRef = useRef(null);
  const noteTextareaRef = useRef(null);
  const prevIsOpenRef = useRef(false);

  // Outreach & SMTP configuration for sender isolation
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_user: '',
    smtp_from_email: '',
    smtp_from_name: '',
  });

  const effectiveSenderEmail = useMemo(() => {
    return (
      smtpConfig.smtp_from_email ||
      smtpConfig.smtp_user ||
      keycloak?.tokenParsed?.email ||
      'sales@company.com'
    );
  }, [smtpConfig.smtp_from_email, smtpConfig.smtp_user, keycloak?.tokenParsed?.email]);

  const effectiveSenderName = useMemo(() => {
    return keycloak?.tokenParsed?.name || smtpConfig.smtp_from_name || 'Sales Representative';
  }, [keycloak?.tokenParsed?.name, smtpConfig.smtp_from_name]);

  // Recipient selection state: defaults to 'ALL'
  const [selectedRecipientId, setSelectedRecipientId] = useState('ALL');
  const [activeQueueIndex, setActiveQueueIndex] = useState(0);
  const [isSendingViaPlatform, setIsSendingViaPlatform] = useState(false);

  // AI Content State
  const [isGenerating, setIsGenerating] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [hasGeneratedAi, setHasGeneratedAi] = useState(false);
  // Dispatched Lead Tracking State
  const [dispatchedLeadIds, setDispatchedLeadIds] = useState(() => {
    const initialDispatched = new Set();
    const existingStatus = touchpoint?.leadOutreachStatus || {};
    Object.entries(existingStatus).forEach(([key, info]) => {
      if (info?.sent === true) {
        initialDispatched.add(key);
      }
    });
    return initialDispatched;
  });
  const [toastMessage, setToastMessage] = useState('');

  const [filterQueueMode, setFilterQueueMode] = useState('all'); // 'all' | 'primary' | 'fallback' | 'unsent'
  const [isAllOutreachFinished, setIsAllOutreachFinished] = useState(false);

  // Auto-resize textareas dynamically with generous line buffer so the bottom text is never clipped
  const autoResizeTextarea = useCallback((element, minHeight = 150) => {
    if (!element) return;
    element.style.height = 'auto';
    const computedHeight = Math.max(element.scrollHeight + 24, minHeight);
    element.style.height = `${computedHeight}px`;
  }, []);

  useEffect(() => {
    if (bodyTextareaRef.current) {
      autoResizeTextarea(bodyTextareaRef.current, 150);
    }
  }, [body, editorMode, activeChannel, isOpen, autoResizeTextarea]);

  useEffect(() => {
    if (noteTextareaRef.current) {
      autoResizeTextarea(noteTextareaRef.current, 130);
    }
  }, [connectionNote, editorMode, linkedInMode, isOpen, autoResizeTextarea]);

  // Step routing breakdown from Step 4
  const stepBreakdown = useMemo(() => {
    return computeStepLeadBreakdown(
      recipients,
      touchpoint?.channel || channel || 'Email',
      touchpoint?.fallbackChannel || 'Auto',
      campaign?.channels && campaign.channels.length > 0
        ? campaign.channels
        : ['Email', 'LinkedIn', 'WhatsApp', 'Phone Call'],
      touchpoint?.stepLeadOverrides || {}
    );
  }, [recipients, touchpoint, channel, campaign]);

  const leadRoutingMap = useMemo(() => {
    const map = new Map();
    (stepBreakdown.allLeads || []).forEach((row) => {
      map.set(String(row.leadId), row);
    });
    return map;
  }, [stepBreakdown]);

  // Helper to collect all identifier keys for a lead (id, _id, email, name)
  const getLeadKeys = useCallback((lead) => {
    if (!lead) return [];
    return [lead.id, lead._id, lead.email, lead.name]
      .filter((k) => k !== undefined && k !== null && String(k).trim() !== '')
      .map(String);
  }, []);

  // Pending leads per channel based on step routing assignment and dispatch status
  const channelPendingCounts = useMemo(() => {
    const counts = {
      Email: 0,
      WhatsApp: 0,
      LinkedIn: 0,
      'Phone Call': 0,
    };
    (stepBreakdown.allLeads || []).forEach((row) => {
      const id = String(row.leadId);
      const rowLead = (recipients || []).find((r) => String(r.id) === id || String(r._id) === id) ||
        row.lead || { id: row.leadId, email: row.email, name: row.leadName };
      const keys = getLeadKeys(rowLead);
      const isDispatched = dispatchedLeadIds.has(id) || keys.some((k) => dispatchedLeadIds.has(k));

      if (!isDispatched && row.assignedChannel && counts[row.assignedChannel] !== undefined) {
        counts[row.assignedChannel] += 1;
      }
    });
    return counts;
  }, [stepBreakdown, dispatchedLeadIds, recipients, getLeadKeys]);

  // Counts of leads dispatched by channel
  const dispatchedByChannel = useMemo(() => {
    const counts = {
      Email: 0,
      WhatsApp: 0,
      LinkedIn: 0,
      'Phone Call': 0,
    };
    (stepBreakdown.allLeads || []).forEach((row) => {
      const id = String(row.leadId);
      const rowLead = (recipients || []).find((r) => String(r.id) === id || String(r._id) === id) ||
        row.lead || { id: row.leadId, email: row.email, name: row.leadName };
      const keys = getLeadKeys(rowLead);
      const isDispatched = dispatchedLeadIds.has(id) || keys.some((k) => dispatchedLeadIds.has(k));

      if (isDispatched && row.assignedChannel && counts[row.assignedChannel] !== undefined) {
        counts[row.assignedChannel] += 1;
      }
    });
    return counts;
  }, [stepBreakdown, dispatchedLeadIds, recipients, getLeadKeys]);

  // Helper to build delivery status map for all recipients
  const buildDeliveryStatusMap = useCallback(
    (dispatchedSet = dispatchedLeadIds) => {
      const deliveryMap = { ...(touchpoint?.leadOutreachStatus || {}) };
      const nowTimeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      (recipients || []).forEach((lead) => {
        const keys = getLeadKeys(lead);
        const isDispatched =
          keys.some((k) => dispatchedSet.has(k)) ||
          (lead.id && dispatchedSet.has(String(lead.id))) ||
          (lead._id && dispatchedSet.has(String(lead._id)));

        keys.forEach((k) => {
          if (isDispatched) {
            deliveryMap[k] = {
              sent: true,
              sentAt: deliveryMap[k]?.sentAt || nowTimeStr,
              channel: deliveryMap[k]?.channel || normalizedChannel,
            };
          } else if (!deliveryMap[k]?.sent) {
            // If not dispatched and not previously sent, explicitly mark as not sent
            deliveryMap[k] = {
              sent: false,
              sentAt: null,
            };
          }
        });
      });

      return deliveryMap;
    },
    [touchpoint?.leadOutreachStatus, recipients, normalizedChannel, dispatchedLeadIds, getLeadKeys]
  );

  const commitDispatchedLeads = useCallback(
    (nextDispatched) => {
      const deliveryMap = buildDeliveryStatusMap(nextDispatched);
      if (typeof onUpdateDispatchedLeads === 'function') {
        onUpdateDispatchedLeads(touchpoint, deliveryMap);
      }
      return deliveryMap;
    },
    [buildDeliveryStatusMap, onUpdateDispatchedLeads, touchpoint]
  );

  // Automatically continues outreach to the next channel in sequence
  const continueToNextChannel = useCallback(
    (nextDispatched) => {
      const undispatchedContactable = (stepBreakdown.allLeads || []).filter((row) => {
        const rowLead = (recipients || []).find(
          (r) => String(r.id) === String(row.leadId) || String(r._id) === String(row.leadId)
        ) || { id: row.leadId, email: row.email, name: row.leadName };
        const keys = getLeadKeys(rowLead);
        const isDispatched =
          keys.some((k) => nextDispatched.has(k)) || nextDispatched.has(String(row.leadId));
        return row.routeType !== 'unreachable' && !isDispatched;
      });

      if (undispatchedContactable.length === 0) {
        setIsAllOutreachFinished(true);
        // Ensure the delivery status is committed immediately to parent
        commitDispatchedLeads(nextDispatched);
        setToastMessage(
          `🎉 All campaign leads finished! All contactable leads have been dispatched across primary and fallback channels.`
        );
        setTimeout(() => setToastMessage(''), 8000);
        return;
      }

      const channelOrder = ['WhatsApp', 'LinkedIn', 'Phone Call', 'Email'];
      let nextTargetChannel = null;
      let nextTargetLeads = [];

      for (const ch of channelOrder) {
        const forCh = undispatchedContactable.filter((row) => row.assignedChannel === ch);
        if (forCh.length > 0) {
          nextTargetChannel = ch;
          nextTargetLeads = forCh;
          break;
        }
      }

      if (!nextTargetChannel) {
        for (const ch of channelOrder) {
          const forCh = undispatchedContactable.filter(
            (row) => row.availableChannels && row.availableChannels.includes(ch)
          );
          if (forCh.length > 0) {
            nextTargetChannel = ch;
            nextTargetLeads = forCh;
            break;
          }
        }
      }

      if (nextTargetChannel) {
        setActiveChannel(nextTargetChannel);
        setFilterQueueMode('fallback');
        setActiveQueueIndex(0);
        setToastMessage(
          `✓ Outreach sent! Continuing to ${nextTargetChannel} for ${nextTargetLeads.length} fallback lead${nextTargetLeads.length > 1 ? 's' : ''}.`
        );
        setTimeout(() => setToastMessage(''), 6000);
      }
    },
    [stepBreakdown, recipients, getLeadKeys, commitDispatchedLeads]
  );

  // Valid leads in cohort with email addresses
  const validLeads = useMemo(() => {
    return (recipients || []).filter((r) => r.email && r.email.includes('@'));
  }, [recipients]);

  // Valid leads in cohort with phone numbers for WhatsApp
  const validWaLeads = useMemo(() => {
    return (recipients || []).filter((r) => Boolean(cleanPhoneNumber(r.phone)));
  }, [recipients]);

  // Valid leads in cohort with phone numbers for Phone Call
  const validPhoneLeads = useMemo(() => {
    return (recipients || []).filter((r) => Boolean(cleanPhoneNumber(r.phone)));
  }, [recipients]);

  // Valid leads in cohort with LinkedIn profile/name for LinkedIn
  const validLinkedInLeads = useMemo(() => {
    return (recipients || []).filter((r) =>
      Boolean(r.linkedinUrl || r.linkedin_url || r.social?.linkedin || r.company || r.name)
    );
  }, [recipients]);

  // Channel availability based strictly on step routing breakdown and dispatched state
  const isEmailChannelAvailable = useMemo(() => {
    const hasAssigned =
      (channelPendingCounts['Email'] || 0) > 0 ||
      (dispatchedByChannel['Email'] || 0) > 0 ||
      (stepBreakdown.allLeads || []).some((r) => r.assignedChannel === 'Email');
    if (hasAssigned) return true;
    if (!stepBreakdown?.allLeads || stepBreakdown.allLeads.length === 0) {
      const hasContact = validLeads.length > 0;
      if (campaign?.channels && campaign.channels.length > 0) {
        return hasContact && campaign.channels.some((c) => /email|gmail/i.test(c));
      }
      return hasContact;
    }
    return false;
  }, [channelPendingCounts, dispatchedByChannel, stepBreakdown, validLeads, campaign]);

  const isWaChannelAvailable = useMemo(() => {
    const hasAssigned =
      (channelPendingCounts['WhatsApp'] || 0) > 0 ||
      (dispatchedByChannel['WhatsApp'] || 0) > 0 ||
      (stepBreakdown.allLeads || []).some((r) => r.assignedChannel === 'WhatsApp');
    if (hasAssigned) return true;
    if (!stepBreakdown?.allLeads || stepBreakdown.allLeads.length === 0) {
      const hasContact = validWaLeads.length > 0;
      if (campaign?.channels && campaign.channels.length > 0) {
        return hasContact && campaign.channels.some((c) => /whatsapp/i.test(c));
      }
      return hasContact;
    }
    return false;
  }, [channelPendingCounts, dispatchedByChannel, stepBreakdown, validWaLeads, campaign]);

  const isLinkedInChannelAvailable = useMemo(() => {
    const hasAssigned =
      (channelPendingCounts['LinkedIn'] || 0) > 0 ||
      (dispatchedByChannel['LinkedIn'] || 0) > 0 ||
      (stepBreakdown.allLeads || []).some((r) => r.assignedChannel === 'LinkedIn');
    if (hasAssigned) return true;
    if (!stepBreakdown?.allLeads || stepBreakdown.allLeads.length === 0) {
      const hasContact = validLinkedInLeads.length > 0;
      if (campaign?.channels && campaign.channels.length > 0) {
        return hasContact && campaign.channels.some((c) => /linkedin/i.test(c));
      }
      return hasContact;
    }
    return false;
  }, [channelPendingCounts, dispatchedByChannel, stepBreakdown, validLinkedInLeads, campaign]);

  const isPhoneChannelAvailable = useMemo(() => {
    const hasAssigned =
      (channelPendingCounts['Phone Call'] || 0) > 0 ||
      (dispatchedByChannel['Phone Call'] || 0) > 0 ||
      (stepBreakdown.allLeads || []).some((r) => r.assignedChannel === 'Phone Call');
    if (hasAssigned) return true;
    if (!stepBreakdown?.allLeads || stepBreakdown.allLeads.length === 0) {
      const hasContact = validPhoneLeads.length > 0;
      if (campaign?.channels && campaign.channels.length > 0) {
        return hasContact && campaign.channels.some((c) => /phone|call/i.test(c));
      }
      return hasContact;
    }
    return false;
  }, [channelPendingCounts, dispatchedByChannel, stepBreakdown, validPhoneLeads, campaign]);

  // Fallback active channel if current channel is unavailable
  useEffect(() => {
    if (!isOpen) return;
    if (isWhatsApp && !isWaChannelAvailable) {
      if (isLinkedInChannelAvailable) setActiveChannel('LinkedIn');
      else if (isEmailChannelAvailable) setActiveChannel('Email');
    } else if (isPhone && !isPhoneChannelAvailable) {
      if (isLinkedInChannelAvailable) setActiveChannel('LinkedIn');
      else if (isEmailChannelAvailable) setActiveChannel('Email');
    } else if (isLinkedIn && !isLinkedInChannelAvailable) {
      if (isEmailChannelAvailable) setActiveChannel('Email');
    } else if (!isPhone && !isWhatsApp && !isLinkedIn && !isEmailChannelAvailable) {
      if (isLinkedInChannelAvailable) setActiveChannel('LinkedIn');
    }
  }, [
    isOpen,
    isWhatsApp,
    isPhone,
    isLinkedIn,
    isWaChannelAvailable,
    isPhoneChannelAvailable,
    isLinkedInChannelAvailable,
    isEmailChannelAvailable,
  ]);

  // Leads strictly assigned/routed to the currently active channel for this touchpoint
  const channelRecipients = useMemo(() => {
    const assigned = (stepBreakdown.allLeads || []).filter(
      (row) => row.assignedChannel === normalizedChannel
    );

    if (assigned.length > 0) {
      return assigned.map((row) => {
        const orig = (recipients || []).find(
          (r) => String(r.id) === String(row.leadId) || String(r._id) === String(row.leadId)
        );
        return (
          orig || {
            id: row.leadId,
            name: row.name || row.leadName,
            company: row.company,
            jobTitle: row.title,
            email: row.email,
            phone: row.phone,
            linkedinUrl: row.linkedinUrl,
            ...row,
          }
        );
      });
    }

    if (isLinkedIn) return validLinkedInLeads;
    if (isWhatsApp) return validWaLeads;
    if (isPhone) return validPhoneLeads;
    return validLeads;
  }, [
    stepBreakdown,
    normalizedChannel,
    recipients,
    isLinkedIn,
    isWhatsApp,
    isPhone,
    validLinkedInLeads,
    validWaLeads,
    validPhoneLeads,
    validLeads,
  ]);

  // Reset queue position and selection when switching active channel
  useEffect(() => {
    if (channelRecipients.length === 1) {
      setSelectedRecipientId(String(channelRecipients[0].id ?? 0));
    } else {
      setSelectedRecipientId('ALL');
    }
    setActiveQueueIndex(0);
  }, [normalizedChannel, channelRecipients.length]);

  // Active recipient resolution
  const activeRecipient = useMemo(() => {
    if (selectedRecipientId === 'ALL') {
      if (channelRecipients.length > 0) {
        const safeIdx = activeQueueIndex % channelRecipients.length;
        return channelRecipients[safeIdx] || channelRecipients[0];
      }
      return null;
    }
    return (
      channelRecipients.find((r) => String(r.id) === String(selectedRecipientId)) ||
      channelRecipients[0] ||
      null
    );
  }, [channelRecipients, selectedRecipientId, activeQueueIndex]);

  // Current lead for sequential loop (Email)
  const currentQueueLead = useMemo(() => {
    if (selectedRecipientId !== 'ALL') {
      return activeRecipient || channelRecipients[0] || null;
    }
    const undispatchedEmail = channelRecipients.filter((r) => !dispatchedLeadIds.has(r.id));
    if (undispatchedEmail.length > 0) {
      const safeIdx = activeQueueIndex % undispatchedEmail.length;
      return undispatchedEmail[safeIdx];
    }
    return (
      channelRecipients[activeQueueIndex % (channelRecipients.length || 1)] ||
      channelRecipients[0] ||
      null
    );
  }, [
    selectedRecipientId,
    activeRecipient,
    channelRecipients,
    activeQueueIndex,
    dispatchedLeadIds,
  ]);

  // Current lead for WhatsApp sequential loop
  const currentWaQueueLead = useMemo(() => {
    if (selectedRecipientId !== 'ALL') {
      return activeRecipient || channelRecipients[0] || null;
    }
    if (channelRecipients.length === 0) {
      return null;
    }
    const undispatchedWa = channelRecipients.filter((r) => !dispatchedLeadIds.has(r.id));
    if (undispatchedWa.length > 0) {
      const safeIdx = activeQueueIndex % undispatchedWa.length;
      return undispatchedWa[safeIdx];
    }
    const safeIdx = activeQueueIndex % channelRecipients.length;
    return channelRecipients[safeIdx] || channelRecipients[0] || null;
  }, [
    selectedRecipientId,
    activeRecipient,
    channelRecipients,
    activeQueueIndex,
    dispatchedLeadIds,
  ]);

  // Current lead for LinkedIn sequential loop
  const currentLinkedInQueueLead = useMemo(() => {
    if (selectedRecipientId !== 'ALL') {
      return activeRecipient || channelRecipients[0] || null;
    }
    if (channelRecipients.length === 0) {
      return null;
    }
    const undispatchedLi = channelRecipients.filter((r) => !dispatchedLeadIds.has(r.id));
    if (undispatchedLi.length > 0) {
      const safeIdx = activeQueueIndex % undispatchedLi.length;
      return undispatchedLi[safeIdx];
    }
    const safeIdx = activeQueueIndex % channelRecipients.length;
    return channelRecipients[safeIdx] || channelRecipients[0] || null;
  }, [
    selectedRecipientId,
    activeRecipient,
    channelRecipients,
    activeQueueIndex,
    dispatchedLeadIds,
  ]);

  // Current lead for Phone Call sequential loop
  const currentPhoneQueueLead = useMemo(() => {
    if (selectedRecipientId !== 'ALL') {
      return activeRecipient || channelRecipients[0] || null;
    }
    if (channelRecipients.length === 0) {
      return null;
    }
    const undispatchedPhone = channelRecipients.filter((r) => !dispatchedLeadIds.has(r.id));
    if (undispatchedPhone.length > 0) {
      const safeIdx = activeQueueIndex % undispatchedPhone.length;
      return undispatchedPhone[safeIdx];
    }
    const safeIdx = activeQueueIndex % channelRecipients.length;
    return channelRecipients[safeIdx] || channelRecipients[0] || null;
  }, [
    selectedRecipientId,
    activeRecipient,
    channelRecipients,
    activeQueueIndex,
    dispatchedLeadIds,
  ]);

  // Campaign Frequency & Timing Configuration
  const campaignTimingConfig = useMemo(() => {
    return {
      ...DEFAULT_TIMING_CONFIG,
      ...(campaign?.schedule || {}),
      ...(campaign?.strategy?.schedule || {}),
    };
  }, [campaign?.schedule, campaign?.strategy?.schedule]);

  const timingGuardrailStatus = useMemo(() => {
    const targetProspect =
      activeRecipient || (recipients && recipients.length > 0 ? recipients[0] : null);
    const tz = resolveEffectiveTimezone({
      prospect: targetProspect,
      config: campaignTimingConfig,
    });
    const now = new Date();
    const windowStatus = isInsideSendingWindow(now, tz, campaignTimingConfig);

    if (windowStatus.inside) {
      return {
        isAllowedNow: true,
        timezone: tz,
        message: `Sending window active (${tz})`,
        nextWindow: null,
      };
    } else {
      const nextResult = calculateNextEligibleSendTime({
        targetTime: now,
        prospectTimezone: tz,
        config: campaignTimingConfig,
      });
      return {
        isAllowedNow: false,
        timezone: tz,
        reason: windowStatus.reason,
        message: `Currently outside allowed window (${windowStatus.reason}). Automated reschedule: ${nextResult.formattedLocal}`,
        nextWindow: nextResult.formattedLocal,
        nextDate: nextResult.nextEligibleTime,
      };
    }
  }, [activeRecipient, recipients, campaignTimingConfig]);

  // Helper to filter recipients based on routing & queue mode
  const getDisplayedRecipients = useCallback(
    (channelName = '') => {
      const channelMatches = (assigned, target) => {
        if (!assigned || !target) return true;
        const a = assigned.toLowerCase();
        const t = target.toLowerCase();
        if (a === t || a.includes(t) || t.includes(a)) return true;
        if (
          (a.includes('phone') || a.includes('call')) &&
          (t.includes('phone') || t.includes('call'))
        ) {
          return true;
        }
        return false;
      };

      return recipients.filter((lead) => {
        const id = String(lead.id);
        const routing = leadRoutingMap.get(id);

        if (filterQueueMode === 'primary') {
          return routing?.routeType === 'primary';
        }
        if (filterQueueMode === 'fallback') {
          if (channelName && routing?.assignedChannel) {
            return (
              routing?.routeType === 'fallback' &&
              channelMatches(routing.assignedChannel, channelName)
            );
          }
          return routing?.routeType === 'fallback';
        }
        if (filterQueueMode === 'unsent') {
          return !dispatchedLeadIds.has(id);
        }
        return true; // 'all'
      });
    },
    [recipients, leadRoutingMap, filterQueueMode, dispatchedLeadIds]
  );

  const displayedLinkedInLeads = useMemo(
    () => getDisplayedRecipients('LinkedIn'),
    [getDisplayedRecipients]
  );
  const displayedWaLeads = useMemo(
    () => getDisplayedRecipients('WhatsApp'),
    [getDisplayedRecipients]
  );
  const displayedEmailLeads = useMemo(
    () => getDisplayedRecipients('Email'),
    [getDisplayedRecipients]
  );
  const displayedPhoneLeads = useMemo(
    () => getDisplayedRecipients('Phone Call'),
    [getDisplayedRecipients]
  );

  const renderLeadRouteChip = (lead) => {
    if (!lead) return null;
    const routing = leadRoutingMap.get(String(lead.id));
    if (!routing) return null;

    if (routing.routeType === 'primary') {
      return (
        <span
          className="cmp-route-chip primary"
          title={`Assigned via primary channel: ${routing.assignedChannel}`}
        >
          <i className="fas fa-bolt" /> Primary
        </span>
      );
    }
    if (routing.routeType === 'fallback') {
      return (
        <span
          className="cmp-route-chip fallback"
          title={routing.reason || `Fallback channel: ${routing.assignedChannel}`}
        >
          <i className="fas fa-exchange-alt" /> Fallback: {routing.assignedChannel}
        </span>
      );
    }
    if (routing.routeType === 'unreachable') {
      return (
        <span
          className="cmp-route-chip unreachable"
          title={routing.reason || 'No contact info available for configured channels'}
        >
          <i className="fas fa-exclamation-triangle" /> Unreachable
        </span>
      );
    }
    return null;
  };

  const renderQueueFilterBar = (targetChannel = '') => {
    const unsentCount = recipients.filter((r) => !dispatchedLeadIds.has(String(r.id))).length;
    const primaryCount =
      stepBreakdown.counts?.primaryCount ?? stepBreakdown.primaryLeads?.length ?? 0;
    const fallbackCount =
      stepBreakdown.counts?.fallbackCount ?? stepBreakdown.fallbackLeads?.length ?? 0;

    return (
      <div className="cmp-queue-filter-bar">
        <div className="cmp-queue-filter-tabs">
          <button
            type="button"
            className={`cmp-filter-pill ${filterQueueMode === 'all' ? 'active' : ''}`}
            onClick={() => setFilterQueueMode('all')}
          >
            All ({recipients.length})
          </button>
          <button
            type="button"
            className={`cmp-filter-pill ${filterQueueMode === 'primary' ? 'active' : ''}`}
            onClick={() => setFilterQueueMode('primary')}
          >
            <i className="fas fa-bolt" /> Primary ({primaryCount})
          </button>
          <button
            type="button"
            className={`cmp-filter-pill ${filterQueueMode === 'fallback' ? 'active' : ''}`}
            onClick={() => setFilterQueueMode('fallback')}
          >
            <i className="fas fa-exchange-alt" /> Fallback ({fallbackCount})
          </button>
          <button
            type="button"
            className={`cmp-filter-pill ${filterQueueMode === 'unsent' ? 'active' : ''}`}
            onClick={() => setFilterQueueMode('unsent')}
          >
            <i className="fas fa-clock" /> Pending Unsent ({unsentCount})
          </button>
        </div>
        {filterQueueMode !== 'all' && (
          <button
            type="button"
            className="cmp-filter-reset-btn"
            onClick={() => setFilterQueueMode('all')}
            title="Reset filter to show all cohort leads"
          >
            <i className="fas fa-times" /> Clear Filter
          </button>
        )}
      </div>
    );
  };

  // Live Call Timer Effect
  useEffect(() => {
    let timer = null;
    if (callStatus === 'connected' || callStatus === 'calling') {
      timer = setInterval(() => {
        setCallSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [callStatus]);

  // Fetch AI content on modal mount or regenerate
  const fetchAiContent = useCallback(async () => {
    if (!isOpen) return;
    setIsGenerating(true);
    setGenerationError('');

    try {
      const response = await axios.post('/campaign/generate-touchpoint-content', {
        channel: normalizedChannel,
        touchpointAngle: touchpoint?.name || '',
        campaignContext: {
          title: campaign?.name || campaign?.title,
          product: campaign?.product,
          productDescription: campaign?.productDescription,
          primaryObjective: campaign?.objective || campaign?.primaryObjective,
          positioning: campaign?.strategy?.positioning,
          painPoints: campaign?.strategy?.painPoints,
          targetPersonas: campaign?.targetPersonas,
          matrix: campaign?.strategy?.matrix,
        },
        recipient: activeRecipient
          ? {
              name: activeRecipient.name,
              company: activeRecipient.company,
              jobTitle: activeRecipient.jobTitle,
            }
          : null,
      });

      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        const targetLeadForGuard = activeRecipient || recipients[0] || null;
        if (isPhone) {
          setPhoneBattlecard({
            opener: data.opener || DEFAULT_PHONE_BATTLECARD.opener,
            valueProp: data.valueProp || DEFAULT_PHONE_BATTLECARD.valueProp,
            qualifyingQuestions:
              data.qualifyingQuestions || DEFAULT_PHONE_BATTLECARD.qualifyingQuestions,
            objectionHandlers: data.objectionHandlers || DEFAULT_PHONE_BATTLECARD.objectionHandlers,
            voicemailScript: data.voicemailScript || DEFAULT_PHONE_BATTLECARD.voicemailScript,
          });
        } else if (isLinkedIn) {
          const note = data.connectionNote || data.message || '';
          setConnectionNote(ensureTemplateTokens(note, targetLeadForGuard));
          setSubject(data.subject || `Inquiry for ${targetLeadForGuard?.company || 'your team'}`);
          setBody(ensureTemplateTokens(data.body || data.message || '', targetLeadForGuard));
        } else if (isWhatsApp) {
          setBody(ensureTemplateTokens(data.message || '', targetLeadForGuard));
        } else {
          setSubject(data.subject || '');
          setBody(ensureTemplateTokens(data.body || '', targetLeadForGuard));
        }
      }
    } catch (err) {
      console.warn('[CampaignOutreachLauncherModal] AI generation error:', err);
      setGenerationError('AI service unavailable; loaded high-converting fallback template.');
      if (isPhone) {
        setPhoneBattlecard(DEFAULT_PHONE_BATTLECARD);
      } else if (isLinkedIn) {
        setConnectionNote(
          `Hi {{firstName}}, noticed your leadership in sales at {{companyName}}. We help B2B teams accelerate outbound pipeline with verified direct dials. Would love to connect!`
        );
        setSubject(
          `${touchpoint?.name ? touchpoint.name.slice(0, 35) : 'Partnership inquiry'} for {{companyName}}`
        );
        setBody(
          `Hi {{firstName}},\n\nI noticed your leadership at {{companyName}}. In relation to ${
            touchpoint?.name || 'accelerating revenue pipeline'
          }, many executive leaders face bottlenecks with manual research and unverified data.\n\nWith ${
            campaign?.product || 'our solution'
          }, teams achieve validated 3.5x pipeline acceleration with verified decision-maker direct dials.\n\nWould you be open to a brief 15-minute consultation this week to explore how this applies to {{companyName}}?\n\nBest regards,\n{{senderName}}`
        );
      } else if (isWhatsApp) {
        setBody(
          `Hi {{firstName}}, saw your team at {{companyName}} is navigating outbound pipeline growth. We help enterprise teams 3.5x meetings with verified decision-maker direct dials. Would you be open to a quick 5-min chat this Thursday?`
        );
      } else {
        setSubject(
          `${touchpoint?.name ? touchpoint.name.slice(0, 35) : 'Quick question'} for {{companyName}}`
        );
        setBody(
          `Hi {{firstName}},\n\nI noticed your leadership in sales at {{companyName}}. In relation to ${
            touchpoint?.name || 'accelerating revenue pipeline'
          }, many executive leaders face bottlenecks with manual research and low connect rates.\n\nWith ${
            campaign?.product || 'our solution'
          }, teams achieve validated 3.5x pipeline acceleration with 100% verified direct dials.\n\nWould you be open to a brief 15-minute consultation this week to explore how this applies to {{companyName}}?\n\nBest regards,\n{{senderName}}`
        );
      }
    } finally {
      setIsGenerating(false);
    }
  }, [
    isOpen,
    normalizedChannel,
    touchpoint?.name,
    campaign?.name,
    campaign?.title,
    campaign?.product,
    campaign?.productDescription,
    campaign?.objective,
    campaign?.primaryObjective,
    campaign?.strategy,
    campaign?.targetPersonas,
    activeRecipient,
    recipients,
    isWhatsApp,
    isLinkedIn,
    isPhone,
  ]);

  const fetchOutreachSettings = useCallback(async () => {
    try {
      const res = await axios.get('/outreach/settings');
      if (res.data?.success && res.data?.data) {
        setSmtpConfig({
          smtp_user: res.data.data.smtp_user || '',
          smtp_from_email: res.data.data.smtp_from_email || '',
          smtp_from_name: res.data.data.smtp_from_name || '',
        });
      }
    } catch (err) {
      console.warn(
        '[CampaignOutreachLauncherModal] Failed to load outreach settings:',
        err.message
      );
    }
  }, []);

  // Initialize dispatchedLeadIds ONLY when modal transitions to open
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      const initialDispatched = new Set();
      const existingStatus = touchpoint?.leadOutreachStatus || {};
      Object.entries(existingStatus).forEach(([key, info]) => {
        if (info?.sent === true) {
          initialDispatched.add(key);
        }
      });
      setDispatchedLeadIds(initialDispatched);
      setIsAllOutreachFinished(false);
      setHasGeneratedAi(false);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, touchpoint?.leadOutreachStatus]);

  // Reset channel-specific session states and fetch channel content when channel changes
  useEffect(() => {
    if (isOpen) {
      setToastMessage('');
      setCallStatus('idle');
      setCallSeconds(0);
      setCallDisposition('');
      setCallNotes('');
      setEditorMode('edit');
      setSubject('');
      setBody('');
      setConnectionNote('');
      setPhoneBattlecard(DEFAULT_PHONE_BATTLECARD);
      setHasGeneratedAi(false);
      fetchOutreachSettings();
    }
  }, [isOpen, normalizedChannel]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateAiContent = useCallback(() => {
    setHasGeneratedAi(true);
    fetchAiContent();
  }, [fetchAiContent]);

  // Personalization preview computed for current active lead
  const previewLead = activeRecipient ||
    (isPhone
      ? currentPhoneQueueLead
      : isLinkedIn
        ? currentLinkedInQueueLead
        : isWhatsApp
          ? currentWaQueueLead
          : currentQueueLead) ||
    recipients[0] || {
      name: 'Yoo Jun Yang',
      company: 'First Pavilion Technology Sdn Bhd',
      jobTitle: 'Software Engineer',
      email: 'yoofpg3@gmail.com',
      phone: '+60 192 153 320',
      linkedinUrl: 'https://www.linkedin.com/in/yoojunyang',
    };

  const previewSubject = interpolateTokens(subject, previewLead, effectiveSenderName);
  const previewBody = interpolateTokens(body, previewLead, effectiveSenderName);
  const previewConnectionNote = truncateForLinkedInNote(
    interpolateTokens(connectionNote, previewLead, effectiveSenderName),
    LINKEDIN_NOTE_CHAR_LIMIT
  );

  // Phone Call Battlecard Preview
  const previewPhoneBattlecard = useMemo(() => {
    const raw = phoneBattlecard || DEFAULT_PHONE_BATTLECARD;
    return {
      opener: interpolateTokens(raw.opener, previewLead, effectiveSenderName),
      valueProp: interpolateTokens(raw.valueProp, previewLead, effectiveSenderName),
      qualifyingQuestions: (raw.qualifyingQuestions || []).map((q) => {
        const qText =
          typeof q === 'string' ? q : q?.question || q?.text || q?.name || String(q || '');
        return interpolateTokens(qText, previewLead, effectiveSenderName);
      }),
      objectionHandlers: {
        competitor: interpolateTokens(
          raw.objectionHandlers?.competitor || '',
          previewLead,
          effectiveSenderName
        ),
        notInterested: interpolateTokens(
          raw.objectionHandlers?.notInterested || '',
          previewLead,
          effectiveSenderName
        ),
        sendEmail: interpolateTokens(
          raw.objectionHandlers?.sendEmail || '',
          previewLead,
          effectiveSenderName
        ),
        noBudget: interpolateTokens(
          raw.objectionHandlers?.noBudget || '',
          previewLead,
          effectiveSenderName
        ),
        badTiming: interpolateTokens(
          raw.objectionHandlers?.badTiming || '',
          previewLead,
          effectiveSenderName
        ),
      },
      voicemailScript: interpolateTokens(
        raw.voicemailScript || '',
        previewLead,
        effectiveSenderName
      ),
    };
  }, [phoneBattlecard, previewLead, effectiveSenderName]);

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Background logging helper
  const logDispatch = async (lead, actionDesc) => {
    try {
      await axios.post('/activity-logs', {
        action: `OUTREACH_${normalizedChannel.toUpperCase()}_DISPATCH`,
        description: actionDesc,
        metadata: {
          campaignId: campaign?.id,
          campaignName: campaign?.name || campaign?.title,
          touchpointStep: touchpoint?.step || 1,
          touchpointName: touchpoint?.name,
          channel: normalizedChannel,
          recipientEmail: lead?.email,
          recipientPhone: lead?.phone,
          recipientName: lead?.name,
          linkedinUrl: lead?.linkedinUrl || lead?.linkedin_url,
        },
      });
    } catch {
      // Non-fatal logging
    }
  };

  const handleModalClose = useCallback(() => {
    const deliveryMap = buildDeliveryStatusMap(dispatchedLeadIds);
    if (typeof onUpdateDispatchedLeads === 'function') {
      onUpdateDispatchedLeads(touchpoint, deliveryMap);
    }
    if (typeof onClose === 'function') {
      onClose(deliveryMap);
    }
  }, [buildDeliveryStatusMap, dispatchedLeadIds, onUpdateDispatchedLeads, touchpoint, onClose]);
  const totalRecipientsCount = channelRecipients.length || 1;
  const currentLeadDisplayIndex =
    channelRecipients.length > 0 ? (activeQueueIndex % channelRecipients.length) + 1 : 1;

  const handlePrevLead = () => {
    if (channelRecipients.length <= 1) return;
    setActiveQueueIndex((prev) => (prev > 0 ? prev - 1 : channelRecipients.length - 1));
  };

  const handleNextLead = () => {
    if (channelRecipients.length <= 1) return;
    setActiveQueueIndex((prev) => (prev + 1) % channelRecipients.length);
  };

  // ── Dynamic Token Insertion Helper ──
  const handleInsertToken = (tokenName) => {
    const tokenText = `{{${tokenName}}}`;
    const activeTarget = lastFocusedInputRef.current;

    if (activeTarget === 'subject' && subjectInputRef.current) {
      const el = subjectInputRef.current;
      const start = el.selectionStart || subject.length;
      const end = el.selectionEnd || subject.length;
      const newSubject = subject.slice(0, start) + tokenText + subject.slice(end);
      setSubject(newSubject);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + tokenText.length, start + tokenText.length);
      }, 50);
    } else if (activeTarget === 'note' && isLinkedIn && noteTextareaRef.current) {
      const el = noteTextareaRef.current;
      const start = el.selectionStart || connectionNote.length;
      const end = el.selectionEnd || connectionNote.length;
      const newNote = connectionNote.slice(0, start) + tokenText + connectionNote.slice(end);
      setConnectionNote(newNote);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + tokenText.length, start + tokenText.length);
      }, 50);
    } else {
      const el = bodyTextareaRef.current;
      if (el) {
        const start = el.selectionStart || body.length;
        const end = el.selectionEnd || body.length;
        const newBody = body.slice(0, start) + tokenText + body.slice(end);
        setBody(newBody);
        setTimeout(() => {
          el.focus();
          el.setSelectionRange(start + tokenText.length, start + tokenText.length);
        }, 50);
      } else {
        setBody((prev) => `${prev} ${tokenText}`);
      }
    }

    setToastMessage(`✓ Inserted ${tokenText} at cursor`);
    setTimeout(() => setToastMessage(''), 2000);
  };

  // ── Quick AI Action: Make Shorter ──
  const handleMakeShorter = () => {
    if (isLinkedIn && linkedInMode === 'connection_note') {
      const shortened = `Hi {{firstName}}, noticed your work at {{companyName}}. We help teams 3.5x pipeline with verified direct dials. Let's connect!`;
      setConnectionNote(shortened);
      setToastMessage('✓ Compressed connection note to high-impact concise format');
    } else if (isWhatsApp) {
      const shortened = `Hi {{firstName}}, saw your team at {{companyName}} is growing. We help B2B teams 3.5x meetings with verified direct dials. Free for a quick 5-min chat this Thursday?`;
      setBody(shortened);
      setToastMessage('✓ Compressed WhatsApp message to <40 words');
    } else {
      const shortened = `Hi {{firstName}},\n\nReaching out because many executives at companies like {{companyName}} are currently tackling outbound predictability and data freshness.\n\nOur platform enables teams to 3.5x qualified meetings with verified direct dials.\n\nAre you available for a 15-minute introductory call this Thursday?`;
      setBody(shortened);
      setToastMessage('✓ Compressed email body to 44 words');
    }
    setTimeout(() => setToastMessage(''), 3500);
  };

  // ── Phone Call Handlers ──
  const handleInitiateCall = (targetLead = null) => {
    const lead = targetLead || previewLead;
    if (!lead?.phone) {
      alert(`No phone number available for ${lead?.name || 'this lead'}.`);
      return;
    }
    const telUri = buildTelUri(lead.phone);
    if (!telUri) {
      alert(`Invalid phone number format for ${lead?.name || 'this lead'}.`);
      return;
    }
    setCallStatus('connected');
    setCallSeconds(0);
    setWhatsAppGuideActive(false);
    window.location.href = telUri;
    setToastMessage(`Dialing ${lead.name} (${lead.phone}) via native calling...`);
    setTimeout(() => setToastMessage(''), 4500);
  };

  const handleInitiateWhatsAppCall = (targetLead = null, useDesktopApp = false) => {
    const lead = targetLead || previewLead;
    if (!lead?.phone) {
      alert(`No phone number available for ${lead?.name || 'this lead'}.`);
      return;
    }

    const cleanPhone = cleanPhoneNumber(lead.phone);
    if (!cleanPhone) {
      alert(`Invalid phone number format for ${lead?.name || 'this lead'}.`);
      return;
    }

    const destinationUrl = useDesktopApp
      ? buildWhatsAppDesktopUri(lead.phone)
      : buildWhatsAppDirectChatUrl(lead.phone);

    setCallStatus('connected');
    setCallSeconds(0);
    setWhatsAppGuideActive(true);

    if (useDesktopApp) {
      window.location.href = destinationUrl;
    } else {
      window.open(destinationUrl, '_blank');
    }

    setToastMessage(
      `Opened WhatsApp chat for ${lead.name}. Click the 📞 Phone icon in WhatsApp's top-right header to start audio call.`
    );
    setTimeout(() => setToastMessage(''), 6000);
  };

  const handleEndCall = () => {
    setCallStatus('ended');
    setWhatsAppGuideActive(false);
    setToastMessage('Call ended. Please select an outcome disposition and save your call notes.');
    setTimeout(() => setToastMessage(''), 4500);
  };

  const handleLogCallOutcome = async (targetLead = null) => {
    const lead = targetLead || previewLead;
    if (!lead) return;
    if (!callDisposition) {
      alert('Please select a call disposition before logging.');
      return;
    }

    const nextDispatched = new Set(dispatchedLeadIds);
    const keys = getLeadKeys(lead);
    keys.forEach((k) => nextDispatched.add(k));
    if (lead.id !== undefined && lead.id !== null) nextDispatched.add(String(lead.id));
    if (lead._id !== undefined && lead._id !== null) nextDispatched.add(String(lead._id));
    if (lead.leadId !== undefined && lead.leadId !== null) nextDispatched.add(String(lead.leadId));
    setDispatchedLeadIds(nextDispatched);
    commitDispatchedLeads(nextDispatched);

    const durationStr = formatTimer(callSeconds);
    const logDesc = `Phone call with ${lead.name} (${lead.phone}): [${callDisposition.toUpperCase()}] Duration: ${durationStr}${callNotes ? ` - Notes: ${callNotes}` : ''}`;

    await logDispatch(lead, logDesc);

    // Reset call session
    setCallStatus('idle');
    setCallSeconds(0);
    setCallDisposition('');
    setCallNotes('');
    setWhatsAppGuideActive(false);

    const phoneLeads = (stepBreakdown.allLeads || []).filter(
      (r) => r.assignedChannel === 'Phone Call'
    );
    const allPhoneDispatched =
      phoneLeads.length > 0 && phoneLeads.every((r) => nextDispatched.has(String(r.leadId)));

    if (allPhoneDispatched) {
      continueToNextChannel(nextDispatched);
    } else {
      setToastMessage(`✓ Logged call with ${lead.name}: ${callDisposition} (${durationStr})`);
      setTimeout(() => setToastMessage(''), 4500);
      if (selectedRecipientId === 'ALL' && channelRecipients.length > 1) {
        const nextIdx = (activeQueueIndex + 1) % channelRecipients.length;
        setActiveQueueIndex(nextIdx);
      }
    }
  };

  // LinkedIn Handler: 1-Click Copy Note & Open Profile
  const handleCopyAndOpenLinkedIn = async (targetLead = null, buttonIdentifier = null) => {
    const lead =
      targetLead ||
      (selectedRecipientId !== 'ALL'
        ? activeRecipient
        : currentLinkedInQueueLead || recipients[0] || previewLead);

    if (!lead) {
      alert('Please select a valid recipient.');
      return;
    }

    const rawNote = (connectionNote && connectionNote.trim()) || DEFAULT_LINKEDIN_NOTE;
    const rawBody = (body && body.trim()) || DEFAULT_LINKEDIN_INMAIL_BODY;

    const messageToCopy =
      linkedInMode === 'connection_note'
        ? truncateForLinkedInNote(
            interpolateTokens(rawNote, lead, effectiveSenderName),
            LINKEDIN_NOTE_CHAR_LIMIT
          )
        : interpolateTokens(rawBody, lead, effectiveSenderName);

    const copySucceeded = await copyTextToClipboard(messageToCopy);

    const profileUrl = buildLinkedInProfileUrl({
      linkedinUrl: lead.linkedinUrl || lead.linkedin_url || lead.social?.linkedin,
      name: lead.name,
      company: lead.company,
    });

    window.open(profileUrl, '_blank');

    const nextDispatched = new Set(dispatchedLeadIds);
    const keys = getLeadKeys(lead);
    keys.forEach((k) => nextDispatched.add(k));
    if (lead.id !== undefined && lead.id !== null) nextDispatched.add(String(lead.id));
    if (lead._id !== undefined && lead._id !== null) nextDispatched.add(String(lead._id));
    if (lead.leadId !== undefined && lead.leadId !== null) nextDispatched.add(String(lead.leadId));
    setDispatchedLeadIds(nextDispatched);
    commitDispatchedLeads(nextDispatched);
    logDispatch(
      lead,
      `Copied personalized ${linkedInMode === 'connection_note' ? 'connection note' : 'InMail'} and opened LinkedIn profile for ${lead.name} (${profileUrl})`
    );

    if (buttonIdentifier) {
      setCopiedButtonId(buttonIdentifier);
      setTimeout(() => setCopiedButtonId(null), 2500);
    }

    const linkedInLeads = (stepBreakdown.allLeads || []).filter(
      (r) => r.assignedChannel === 'LinkedIn'
    );
    const allLiDispatched =
      linkedInLeads.length > 0 && linkedInLeads.every((r) => nextDispatched.has(String(r.leadId)));

    if (allLiDispatched) {
      continueToNextChannel(nextDispatched);
    } else {
      const snippet =
        messageToCopy.length > 38 ? `${messageToCopy.slice(0, 38)}...` : messageToCopy;
      if (copySucceeded) {
        setToastMessage(`✓ Copied note ("${snippet}") & opened LinkedIn for ${lead.name}!`);
      } else {
        setToastMessage(
          `Opened LinkedIn for ${lead.name}. Note: Automatic clipboard blocked by browser; please copy note from preview.`
        );
      }
      setTimeout(() => setToastMessage(''), 5500);
    }
  };

  const handleCopyNoteOnly = async (buttonIdentifier = 'preview-copy') => {
    const lead =
      (isLinkedIn
        ? currentLinkedInQueueLead
        : isWhatsApp
          ? currentWaQueueLead
          : currentQueueLead) ||
      activeRecipient ||
      previewLead;

    const rawNote = (connectionNote && connectionNote.trim()) || DEFAULT_LINKEDIN_NOTE;
    const rawBody = (body && body.trim()) || DEFAULT_LINKEDIN_INMAIL_BODY;

    const messageToCopy =
      linkedInMode === 'connection_note'
        ? truncateForLinkedInNote(
            interpolateTokens(rawNote, lead, effectiveSenderName),
            LINKEDIN_NOTE_CHAR_LIMIT
          )
        : interpolateTokens(rawBody, lead, effectiveSenderName);

    const copySucceeded = await copyTextToClipboard(messageToCopy);
    if (buttonIdentifier) {
      setCopiedButtonId(buttonIdentifier);
      setTimeout(() => setCopiedButtonId(null), 2500);
    }

    const snippet = messageToCopy.length > 40 ? `${messageToCopy.slice(0, 40)}...` : messageToCopy;
    if (copySucceeded) {
      setToastMessage(`✓ Copied note to clipboard: "${snippet}"`);
    } else {
      setToastMessage(
        'Unable to copy automatically. Please select text from preview box and copy manually.'
      );
    }
    setTimeout(() => setToastMessage(''), 5000);
  };

  const handleNextLinkedInLead = async (specificLead = null, buttonIdentifier = null) => {
    const lead = specificLead || currentLinkedInQueueLead;
    if (!lead) return;

    await handleCopyAndOpenLinkedIn(lead, buttonIdentifier);

    if (selectedRecipientId === 'ALL' && channelRecipients.length > 1) {
      const nextIdx = (activeQueueIndex + 1) % channelRecipients.length;
      setActiveQueueIndex(nextIdx);
      const nextLead = channelRecipients[nextIdx];
      setToastMessage(
        `✓ Copied note & opened LinkedIn for ${lead.name}! Next in queue: ${nextLead?.name || 'Complete'}`
      );
      setTimeout(() => setToastMessage(''), 4500);
    }
  };

  // Option A: Automated Direct Platform Send
  const handleSendBatchPlatform = async (targetLead = null) => {
    const isSingle = Boolean(targetLead || selectedRecipientId !== 'ALL');
    const leadsToSend = isSingle
      ? [targetLead || activeRecipient || currentQueueLead].filter(Boolean)
      : channelRecipients;

    if (leadsToSend.length === 0) {
      alert(`No valid leads found for ${normalizedChannel} outreach.`);
      return;
    }

    if (!subject.trim() || !body.trim()) {
      alert('Please provide both subject line and email body content.');
      return;
    }

    setIsSendingViaPlatform(true);
    try {
      const payloadLeads = leadsToSend.map((l) => ({
        id: l.id,
        name: l.name,
        first_name: l.firstName || (l.name ? l.name.split(' ')[0] : 'there'),
        last_name:
          l.lastName ||
          (l.name && l.name.includes(' ') ? l.name.split(' ').slice(1).join(' ') : ''),
        company_name: l.company,
        company: l.company,
        email: l.email,
        phone: l.phone,
      }));

      const response = await axios.post('/outreach/send', {
        prospectIds: payloadLeads.map((l) => String(l.id)),
        prospects: payloadLeads,
        subject,
        message: body,
        senderName: effectiveSenderName,
        senderEmail: effectiveSenderEmail,
      });

      if (response.data?.success) {
        const result = response.data.data;
        const nextDispatched = new Set(dispatchedLeadIds);
        leadsToSend.forEach((l) => {
          const keys = getLeadKeys(l);
          keys.forEach((k) => nextDispatched.add(k));
        });
        setDispatchedLeadIds(nextDispatched);
        commitDispatchedLeads(nextDispatched);

        leadsToSend.forEach((l) => {
          logDispatch(l, `Automated platform email dispatched to ${l.name} (${l.email})`);
        });

        if (result.failed > 0 && result.sent === 0) {
          alert(`Failed to dispatch emails:\n${(result.errors || []).join('\n')}`);
        } else if (result.failed > 0) {
          setToastMessage(`✓ Sent ${result.sent} emails successfully (${result.failed} failed).`);
          continueToNextChannel(nextDispatched);
        } else {
          continueToNextChannel(nextDispatched);
        }
      }
    } catch (err) {
      console.error('[CampaignOutreachLauncherModal] Platform dispatch error:', err);
      const isConfigError = !err.response || err.response.status >= 400;
      const confirmFallback = window.confirm(
        'Direct email sending failed or custom SMTP/mail server is not configured.\n\n' +
          `Would you like to mark all ${leadsToSend.length} lead(s) as sent and commit the delivery status so this touchpoint updates to Completed?`
      );

      if (confirmFallback) {
        const nextDispatched = new Set(dispatchedLeadIds);
        leadsToSend.forEach((l) => {
          const keys = getLeadKeys(l);
          keys.forEach((k) => nextDispatched.add(k));
          logDispatch(
            l,
            `Manual/batch outreach marked as sent to ${l.name} (${l.email || l.phone || 'Lead'})`
          );
        });
        setDispatchedLeadIds(nextDispatched);
        commitDispatchedLeads(nextDispatched);
        setToastMessage(`✓ Marked ${leadsToSend.length} leads as sent!`);
        continueToNextChannel(nextDispatched);
      } else {
        const msg =
          err.response?.data?.error ||
          'Failed to dispatch emails via platform. Please check SMTP / mail server configuration.';
        alert(msg);
      }
    } finally {
      setIsSendingViaPlatform(false);
      setTimeout(() => setToastMessage(''), 5000);
    }
  };

  // Option B: Interactive 1-by-1 Gmail Web Sequential Dispatch Loop
  const handleNextGmailLead = (specificLead = null) => {
    const lead = specificLead || currentQueueLead;
    if (!lead || !lead.email) {
      alert('No valid lead email found.');
      return;
    }

    const finalSubj = interpolateTokens(subject, lead, effectiveSenderName);
    const finalMsg = interpolateTokens(body, lead, effectiveSenderName);
    const gmailUrl = buildIsolatedGmailComposeUrl({
      to: lead.email,
      subject: finalSubj,
      body: finalMsg,
      senderEmail: effectiveSenderEmail,
    });

    window.open(gmailUrl, '_blank');

    const nextDispatched = new Set(dispatchedLeadIds);
    const keys = getLeadKeys(lead);
    keys.forEach((k) => nextDispatched.add(k));
    setDispatchedLeadIds(nextDispatched);
    commitDispatchedLeads(nextDispatched);
    logDispatch(lead, `Dispatched Step 1 email via Gmail Web to ${lead.name} (${lead.email})`);

    const emailLeads = (stepBreakdown.allLeads || []).filter((r) => r.assignedChannel === 'Email');
    const allEmailDispatched =
      emailLeads.length > 0 && emailLeads.every((r) => nextDispatched.has(String(r.leadId)));

    if (allEmailDispatched) {
      continueToNextChannel(nextDispatched);
    } else {
      if (selectedRecipientId === 'ALL' && channelRecipients.length > 1) {
        const nextIdx = (activeQueueIndex + 1) % channelRecipients.length;
        setActiveQueueIndex(nextIdx);
        const nextLead = channelRecipients[nextIdx];
        setToastMessage(
          `Opened Gmail for ${lead.name}! Next in queue: ${nextLead?.name || 'Complete'}`
        );
      } else {
        setToastMessage(`Opened Gmail compose draft for ${lead.name}!`);
      }
      setTimeout(() => setToastMessage(''), 4500);
    }
  };

  // Single Lead or Default Mail Client Dispatch
  const handleSendEmail = (targetLead = null, openGmail = false) => {
    const effectiveLead =
      targetLead ||
      (selectedRecipientId !== 'ALL'
        ? activeRecipient
        : currentQueueLead || channelRecipients[0] || recipients[0] || previewLead);

    if (!effectiveLead?.email || !effectiveLead.email.includes('@')) {
      alert(`No valid email address found for ${effectiveLead?.name || 'the selected lead'}.`);
      return;
    }

    const finalSubj = interpolateTokens(subject, effectiveLead, effectiveSenderName);
    const finalMsg = interpolateTokens(body, effectiveLead, effectiveSenderName);

    if (openGmail) {
      const gmailUrl = buildIsolatedGmailComposeUrl({
        to: effectiveLead.email,
        subject: finalSubj,
        body: finalMsg,
        senderEmail: effectiveSenderEmail,
      });
      window.open(gmailUrl, '_blank');
    } else {
      const mailtoUrl = `mailto:${encodeURIComponent(effectiveLead.email)}?subject=${encodeURIComponent(
        finalSubj
      )}&body=${encodeURIComponent(finalMsg)}`;
      window.location.href = mailtoUrl;
    }

    const nextDispatched = new Set(dispatchedLeadIds);
    const keys = getLeadKeys(effectiveLead);
    keys.forEach((k) => nextDispatched.add(k));
    if (effectiveLead.id !== undefined && effectiveLead.id !== null)
      nextDispatched.add(String(effectiveLead.id));
    if (effectiveLead._id !== undefined && effectiveLead._id !== null)
      nextDispatched.add(String(effectiveLead._id));
    if (effectiveLead.leadId !== undefined && effectiveLead.leadId !== null)
      nextDispatched.add(String(effectiveLead.leadId));
    setDispatchedLeadIds(nextDispatched);
    commitDispatchedLeads(nextDispatched);
    logDispatch(
      effectiveLead,
      `Dispatched Step 1 email to ${effectiveLead.name} (${effectiveLead.email})`
    );

    const emailLeads = (stepBreakdown.allLeads || []).filter((r) => r.assignedChannel === 'Email');
    const allEmailDispatched =
      emailLeads.length > 0 && emailLeads.every((r) => nextDispatched.has(String(r.leadId)));

    if (allEmailDispatched) {
      continueToNextChannel(nextDispatched);
    } else {
      setToastMessage(
        `Opened ${openGmail ? 'Gmail Web (Isolated)' : 'Email client'} for ${effectiveLead.name} with personalized message!`
      );
      setTimeout(() => setToastMessage(''), 4000);
    }
  };

  const recordWhatsAppDispatch = (lead) => {
    if (!lead) return;
    const cleanPhone = cleanPhoneNumber(lead.phone);
    const nextDispatched = new Set(dispatchedLeadIds);
    const keys = getLeadKeys(lead);
    keys.forEach((k) => nextDispatched.add(k));
    if (lead.id !== undefined && lead.id !== null) nextDispatched.add(String(lead.id));
    if (lead._id !== undefined && lead._id !== null) nextDispatched.add(String(lead._id));
    if (lead.leadId !== undefined && lead.leadId !== null) nextDispatched.add(String(lead.leadId));
    setDispatchedLeadIds(nextDispatched);
    commitDispatchedLeads(nextDispatched);
    logDispatch(lead, `Opened WhatsApp chat for ${lead.name} (${cleanPhone})`);

    const waLeads = (stepBreakdown.allLeads || []).filter((r) => r.assignedChannel === 'WhatsApp');
    const allWaDispatched =
      waLeads.length > 0 && waLeads.every((r) => nextDispatched.has(String(r.leadId)));

    if (allWaDispatched) {
      continueToNextChannel(nextDispatched);
    } else {
      setToastMessage(`Opened WhatsApp Web for ${lead.name} with pre-filled message!`);
      setTimeout(() => setToastMessage(''), 4000);
    }
  };

  const footerWaLead = currentWaQueueLead || previewLead || null;
  const footerWaCleanPhone = footerWaLead?.phone ? cleanPhoneNumber(footerWaLead.phone) : '';
  const footerWaUrl =
    isWhatsApp && footerWaCleanPhone
      ? buildWhatsAppClickToChatUrl({
          phone: footerWaCleanPhone,
          message: interpolateTokens(body, footerWaLead, effectiveSenderName),
        })
      : '';

  const wordCount = body.trim() ? body.trim().split(/\s+/).length : 0;
  const noteCharCount = connectionNote.trim() ? connectionNote.length : 0;

  const leadProfileUrl = buildLinkedInProfileUrl({
    linkedinUrl:
      previewLead.linkedinUrl || previewLead.linkedin_url || previewLead.social?.linkedin,
    name: previewLead.name,
    company: previewLead.company,
  });

  const leadInitial =
    previewLead.name && previewLead.name.trim()
      ? previewLead.name.trim().charAt(0).toUpperCase()
      : 'P';

  const campaignTitleDisplay =
    campaign?.name ||
    campaign?.title ||
    'First Pavilion Technology Generation - Software Engineer Recruitment Campaign';

  const touchpointAngleDisplay =
    touchpoint?.name ||
    'Value Proposition: Skip the 3-6 Month Promotion Wait – Get Promoted in 12 Months at First Pavilion';

  if (!isOpen) return null;

  return (
    <div className="cmp-outreach-modal-overlay" role="dialog" aria-modal="true">
      <div className="cmp-outreach-modal-container">
        {/* ── Top Header ── */}
        <div
          className={`cmp-outreach-modal-header ${
            isPhone ? 'phone' : isLinkedIn ? 'linkedin' : isWhatsApp ? 'whatsapp' : 'email'
          }`}
        >
          <div className="cmp-outreach-header-left">
            <div className="cmp-channel-avatar">
              <i
                className={
                  isPhone
                    ? 'fas fa-phone-alt'
                    : isLinkedIn
                      ? 'fab fa-linkedin-in'
                      : isWhatsApp
                        ? 'fab fa-whatsapp'
                        : 'fas fa-envelope'
                }
              />
            </div>
            <div className="cmp-outreach-header-titles">
              <div className="cmp-outreach-title-row">
                <h3 className="cmp-outreach-modal-title">
                  Launch Outreach:{' '}
                  <span className="cmp-channel-title-highlight">{normalizedChannel}</span>
                </h3>
              </div>
              <div className="cmp-outreach-modal-sub">
                <span className="cmp-sub-campaign-title">{campaignTitleDisplay}</span>
                <span className="cmp-sub-separator">/</span>
                <span className="cmp-sub-angle-title">{touchpointAngleDisplay}</span>
              </div>
            </div>
          </div>

          <div className="cmp-outreach-header-right">
            {/* Channel Switcher Tabs Group */}
            <div className="cmp-channel-switch-tabs">
              <button
                type="button"
                className={`cmp-channel-tab-btn ${
                  !isPhone && !isWhatsApp && !isLinkedIn ? 'active email' : ''
                } ${!isEmailChannelAvailable ? 'disabled' : ''}`}
                onClick={() => {
                  if (!isEmailChannelAvailable) return;
                  setActiveChannel('Email');
                }}
                disabled={!isEmailChannelAvailable}
                title={
                  isEmailChannelAvailable
                    ? 'Gmail / Email Outreach'
                    : 'No email addresses available in current contact list'
                }
              >
                <i className="fas fa-envelope" /> Gmail / Email
                {channelPendingCounts['Email'] > 0 ? (
                  <span
                    className="cmp-tab-pending-pill"
                    title={`${channelPendingCounts['Email']} leads pending email`}
                  >
                    {channelPendingCounts['Email']}
                  </span>
                ) : dispatchedByChannel['Email'] > 0 ? (
                  <span className="cmp-tab-done-pill" title="All email leads dispatched">
                    <i className="fas fa-check" />
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                className={`cmp-channel-tab-btn ${isWhatsApp ? 'active whatsapp' : ''} ${
                  !isWaChannelAvailable ? 'disabled' : ''
                }`}
                onClick={() => {
                  if (!isWaChannelAvailable) return;
                  setActiveChannel('WhatsApp');
                }}
                disabled={!isWaChannelAvailable}
                title={
                  isWaChannelAvailable
                    ? 'WhatsApp Outreach'
                    : 'No phone numbers available in current contact list'
                }
              >
                <i className="fab fa-whatsapp" /> WhatsApp
                {channelPendingCounts['WhatsApp'] > 0 ? (
                  <span
                    className="cmp-tab-pending-pill"
                    title={`${channelPendingCounts['WhatsApp']} leads pending WhatsApp`}
                  >
                    {channelPendingCounts['WhatsApp']}
                  </span>
                ) : dispatchedByChannel['WhatsApp'] > 0 ? (
                  <span className="cmp-tab-done-pill" title="All WhatsApp leads dispatched">
                    <i className="fas fa-check" />
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                className={`cmp-channel-tab-btn ${isLinkedIn ? 'active linkedin' : ''} ${
                  !isLinkedInChannelAvailable ? 'disabled' : ''
                }`}
                onClick={() => {
                  if (!isLinkedInChannelAvailable) return;
                  setActiveChannel('LinkedIn');
                  if (!connectionNote || !connectionNote.trim()) {
                    setConnectionNote(DEFAULT_LINKEDIN_NOTE);
                  }
                }}
                disabled={!isLinkedInChannelAvailable}
                title={
                  isLinkedInChannelAvailable
                    ? 'LinkedIn Outreach'
                    : 'No LinkedIn profiles available in current contact list'
                }
              >
                <i className="fab fa-linkedin-in" /> LinkedIn
                {channelPendingCounts['LinkedIn'] > 0 ? (
                  <span
                    className="cmp-tab-pending-pill"
                    title={`${channelPendingCounts['LinkedIn']} leads pending LinkedIn`}
                  >
                    {channelPendingCounts['LinkedIn']}
                  </span>
                ) : dispatchedByChannel['LinkedIn'] > 0 ? (
                  <span className="cmp-tab-done-pill" title="All LinkedIn leads dispatched">
                    <i className="fas fa-check" />
                  </span>
                ) : null}
              </button>
              <button
                type="button"
                className={`cmp-channel-tab-btn ${isPhone ? 'active phone' : ''} ${
                  !isPhoneChannelAvailable ? 'disabled' : ''
                }`}
                onClick={() => {
                  if (!isPhoneChannelAvailable) return;
                  setActiveChannel('Phone Call');
                }}
                disabled={!isPhoneChannelAvailable}
                title={
                  isPhoneChannelAvailable
                    ? 'Phone Call Battlecard'
                    : 'No phone numbers available in current contact list'
                }
              >
                <i className="fas fa-phone-alt" /> Phone Call
                {channelPendingCounts['Phone Call'] > 0 ? (
                  <span
                    className="cmp-tab-pending-pill"
                    title={`${channelPendingCounts['Phone Call']} leads pending phone`}
                  >
                    {channelPendingCounts['Phone Call']}
                  </span>
                ) : dispatchedByChannel['Phone Call'] > 0 ? (
                  <span className="cmp-tab-done-pill" title="All phone call leads dispatched">
                    <i className="fas fa-check" />
                  </span>
                ) : null}
              </button>
            </div>

            <button
              type="button"
              className="cmp-outreach-close-btn"
              onClick={handleModalClose}
              aria-label="Close modal"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        {/* ── TO & Stepper Navigation Bar ── */}
        <div className="cmp-outreach-to-bar">
          <div className="cmp-to-bar-left">
            <span className="cmp-to-label">TO:</span>
            <div className="cmp-to-select-wrapper">
              <select
                className="cmp-outreach-select cmp-to-select"
                value={selectedRecipientId}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedRecipientId(val);
                  if (val !== 'ALL') {
                    const foundIdx = channelRecipients.findIndex(
                      (r) => String(r.id) === String(val)
                    );
                    if (foundIdx !== -1) {
                      setActiveQueueIndex(foundIdx);
                    }
                  }
                }}
              >
                {channelRecipients.length > 1 && (
                  <option value="ALL">
                    All {normalizedChannel} Leads ({channelRecipients.length} Prospects)
                  </option>
                )}
                {channelRecipients.map((r, i) => (
                  <option key={r.id || i} value={r.id || i}>
                    {r.name} &mdash; {r.company} (
                    {isPhone
                      ? r.phone || 'No phone'
                      : isLinkedIn
                        ? r.linkedinUrl
                          ? 'Profile Linked'
                          : 'Search Fallback'
                        : isWhatsApp
                          ? r.phone || 'No phone'
                          : r.email || 'No email'}
                    )
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedRecipientId === 'ALL' && (
            <div className="cmp-to-bar-right">
              <div className="cmp-stepper-control-group">
                <button
                  type="button"
                  className="cmp-stepper-btn"
                  onClick={handlePrevLead}
                  disabled={channelRecipients.length <= 1}
                  title="Previous Prospect"
                >
                  <i className="fas fa-chevron-left" />
                </button>
                <span className="cmp-stepper-counter">
                  {currentLeadDisplayIndex} of {totalRecipientsCount}
                </span>
                <button
                  type="button"
                  className="cmp-stepper-btn"
                  onClick={handleNextLead}
                  disabled={channelRecipients.length <= 1}
                  title="Next Prospect"
                >
                  <i className="fas fa-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </div>

        {toastMessage && (
          <div className="cmp-outreach-toast-banner" role="status">
            <i className="fas fa-check-circle" />
            <span>{toastMessage}</span>
          </div>
        )}

        {/* Outreach Timing Guardrail Status Bar */}
        <div
          className={`cmp-timing-status-bar ${timingGuardrailStatus.isAllowedNow ? 'allowed' : 'rescheduled'}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
            padding: '8px 16px',
            fontSize: '11.5px',
            background: timingGuardrailStatus.isAllowedNow ? '#f0fdf4' : '#fffbeb',
            borderBottom: timingGuardrailStatus.isAllowedNow
              ? '1px solid #bbf7d0'
              : '1px solid #fde68a',
            color: timingGuardrailStatus.isAllowedNow ? '#166534' : '#92400e',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i
              className={
                timingGuardrailStatus.isAllowedNow
                  ? 'fas fa-check-circle text-success'
                  : 'fas fa-shield-alt text-warning'
              }
            />
            <span>
              <strong>Outreach Timing Guardrail:</strong> {timingGuardrailStatus.message}
            </span>
          </div>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
            Sending Policy: {campaignTimingConfig.sendWindowStart || '09:00'}–
            {campaignTimingConfig.sendWindowEnd || '18:00'} &bull; Max{' '}
            {campaignTimingConfig.maxTouchesPerPeriod || 3} touches /{' '}
            {campaignTimingConfig.periodDays || 7}d
          </span>
        </div>

        {/* Modal Body */}
        {/* ── Main Two-Column Body ── */}
        <div className="cmp-outreach-modal-body">
          {/* ── Left Column: Prospect Context & Dynamic Tokens ── */}
          <div className="cmp-outreach-left-col">
            {/* Card 1: Prospect Profile Card */}
            <div className="cmp-prospect-profile-card">
              <div className="cmp-prospect-card-header">
                <div className="cmp-prospect-avatar">{leadInitial}</div>
                <div className="cmp-prospect-header-text">
                  <h4 className="cmp-prospect-name">{previewLead.name || 'Lead Name'}</h4>
                  <p className="cmp-prospect-subtitle">
                    {previewLead.jobTitle || 'Decision Maker'} &bull;{' '}
                    <strong>{previewLead.company || 'Company'}</strong>
                  </p>
                </div>
              </div>

              <div className="cmp-prospect-contact-details">
                {/* Email Row */}
                <div className="cmp-contact-detail-row">
                  <i className="fas fa-envelope cmp-contact-icon email" />
                  {previewLead.email ? (
                    <span className="cmp-contact-text email">{previewLead.email}</span>
                  ) : (
                    <span className="cmp-no-email-badge">
                      <i className="fas fa-exclamation-circle" /> No Email
                    </span>
                  )}
                </div>

                {/* Phone Row */}
                <div className="cmp-contact-detail-row">
                  <i className="fas fa-phone-alt cmp-contact-icon phone" />
                  {previewLead.phone ? (
                    <div className="cmp-phone-with-rfc">
                      <span className="cmp-contact-text phone">{previewLead.phone}</span>
                      <span className="cmp-rfc-tag">RFC 3966</span>
                    </div>
                  ) : (
                    <span className="cmp-no-email-badge">
                      <i className="fas fa-exclamation-circle" /> No Phone
                    </span>
                  )}
                </div>

                {/* LinkedIn Row */}
                <div className="cmp-contact-detail-row">
                  <i className="fab fa-linkedin-in cmp-contact-icon linkedin" />
                  <a
                    href={leadProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cmp-contact-link linkedin"
                  >
                    View LinkedIn Profile <i className="fas fa-external-link-alt" />
                  </a>
                </div>

                {/* Prospect Local Time Row */}
                <div className="cmp-contact-detail-row">
                  <i
                    className="fas fa-globe-americas cmp-contact-icon"
                    style={{ color: '#0284c7' }}
                  />
                  <span
                    className="cmp-contact-text"
                    style={{ fontSize: '11.5px', color: '#334155' }}
                  >
                    Local Time:{' '}
                    <strong>
                      {formatLocalDateTimeInTz(
                        new Date(),
                        timingGuardrailStatus.timezone ||
                          resolveEffectiveTimezone({
                            prospect: previewLead,
                            config: campaignTimingConfig,
                          })
                      )}
                    </strong>
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Outreach Strategy Angle Card */}
            <div className="cmp-outreach-angle-card">
              <div className="cmp-card-section-title">
                <i className="fas fa-bullseye" /> OUTREACH STRATEGY ANGLE
              </div>
              <p className="cmp-angle-card-content">{touchpointAngleDisplay}</p>
            </div>

            {/* Card 3: Dynamic Personalization Tokens Card */}
            <div className="cmp-dynamic-tokens-card">
              <div className="cmp-card-section-title">
                <i className="fas fa-code" /> DYNAMIC PERSONALIZATION TOKENS
              </div>
              <div className="cmp-tokens-sub-instruction">
                Click any token to insert at cursor position:
              </div>

              <div className="cmp-tokens-2x2-grid">
                <button
                  type="button"
                  className="cmp-token-chip-btn"
                  onClick={() => handleInsertToken('firstName')}
                  title={`Insert {{firstName}} (Resolves to: ${previewLead.firstName || (previewLead.name ? previewLead.name.split(' ')[0] : 'First Name')})`}
                >
                  <span className="cmp-token-chip-tag">{`{{firstName}}`}</span>
                  <span className="cmp-token-chip-val">
                    {previewLead.firstName ||
                      (previewLead.name ? previewLead.name.split(' ')[0] : 'Yoo')}
                  </span>
                </button>

                <button
                  type="button"
                  className="cmp-token-chip-btn"
                  onClick={() => handleInsertToken('companyName')}
                  title={`Insert {{companyName}} (Resolves to: ${previewLead.company || 'Company'})`}
                >
                  <span className="cmp-token-chip-tag">{`{{companyName}}`}</span>
                  <span className="cmp-token-chip-val">
                    {previewLead.company || 'First Pavilion Technology Sdn Bhd'}
                  </span>
                </button>

                <button
                  type="button"
                  className="cmp-token-chip-btn"
                  onClick={() => handleInsertToken('jobTitle')}
                  title={`Insert {{jobTitle}} (Resolves to: ${previewLead.jobTitle || 'Role'})`}
                >
                  <span className="cmp-token-chip-tag">{`{{jobTitle}}`}</span>
                  <span className="cmp-token-chip-val">
                    {previewLead.jobTitle || 'Software Engineer'}
                  </span>
                </button>

                <button
                  type="button"
                  className="cmp-token-chip-btn"
                  onClick={() => handleInsertToken('senderName')}
                  title={`Insert {{senderName}} (Resolves to: ${effectiveSenderName})`}
                >
                  <span className="cmp-token-chip-tag">{`{{senderName}}`}</span>
                  <span className="cmp-token-chip-val">{effectiveSenderName}</span>
                </button>
              </div>
            </div>

            {/* Multi-Channel Fallback Progression Bar */}
            {(stepBreakdown.counts?.fallbackCount ?? stepBreakdown.fallbackLeads?.length ?? 0) >
              0 && (
              <div className="cmp-fallback-progression-bar">
                <div
                  className={`cmp-progression-step-node ${
                    normalizedChannel === (stepBreakdown.primaryChannel || 'Email')
                      ? 'active'
                      : dispatchedByChannel[stepBreakdown.primaryChannel || 'Email'] > 0
                        ? 'completed'
                        : ''
                  }`}
                >
                  <span className="cmp-prog-node-icon">
                    {dispatchedByChannel[stepBreakdown.primaryChannel || 'Email'] > 0 ? (
                      <i className="fas fa-check text-success" />
                    ) : (
                      <i className="fas fa-bullseye" />
                    )}
                  </span>
                  <div className="cmp-prog-node-text">
                    <strong>Primary: {stepBreakdown.primaryChannel || 'Email'}</strong>
                    <span>
                      {stepBreakdown.counts?.primaryCount ??
                        stepBreakdown.primaryLeads?.length ??
                        0}{' '}
                      leads
                    </span>
                  </div>
                </div>

                <div className="cmp-progression-connector">
                  <i className="fas fa-chevron-right" />
                </div>

                <div
                  className={`cmp-progression-step-node ${
                    normalizedChannel !== (stepBreakdown.primaryChannel || 'Email') &&
                    !isAllOutreachFinished
                      ? 'active'
                      : isAllOutreachFinished
                        ? 'completed'
                        : ''
                  }`}
                >
                  <span className="cmp-prog-node-icon">
                    {isAllOutreachFinished ? (
                      <i className="fas fa-check text-success" />
                    ) : (
                      <i className="fas fa-exchange-alt" />
                    )}
                  </span>
                  <div className="cmp-prog-node-text">
                    <strong>Fallback ({touchpoint?.fallbackChannel || 'Auto'})</strong>
                    <span>
                      {stepBreakdown.counts?.fallbackCount ??
                        stepBreakdown.fallbackLeads?.length ??
                        0}{' '}
                      leads
                    </span>
                  </div>
                </div>

                <div className="cmp-progression-connector">
                  <i className="fas fa-chevron-right" />
                </div>

                <div
                  className={`cmp-progression-step-node ${isAllOutreachFinished ? 'completed' : ''}`}
                >
                  <span className="cmp-prog-node-icon">
                    <i className="fas fa-flag-checkered" />
                  </span>
                  <div className="cmp-prog-node-text">
                    <strong>Complete Cohort</strong>
                    <span>{recipients.length} leads</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Right Column: Outreach Studio & Editor/Preview ── */}
          <div className="cmp-outreach-right-col">
            <div className="cmp-studio-card">
              {/* Studio Card Header */}
              <div className="cmp-studio-header">
                {isLinkedIn ? (
                  <div className="cmp-linkedin-mode-toggle">
                    <button
                      type="button"
                      className={`cmp-linkedin-mode-btn ${
                        linkedInMode === 'connection_note' ? 'active' : ''
                      }`}
                      onClick={() => setLinkedInMode('connection_note')}
                    >
                      <i className="fas fa-user-plus" /> Connection Note (Max 300)
                    </button>
                    <button
                      type="button"
                      className={`cmp-linkedin-mode-btn ${
                        linkedInMode === 'inmail' ? 'active' : ''
                      }`}
                      onClick={() => setLinkedInMode('inmail')}
                    >
                      <i className="fas fa-paper-plane" /> InMail Message
                    </button>
                  </div>
                ) : (
                  <div className="cmp-studio-title">
                    <i
                      className={
                        isPhone
                          ? 'fas fa-phone-alt'
                          : isWhatsApp
                            ? 'fab fa-whatsapp'
                            : 'fas fa-envelope'
                      }
                    />{' '}
                    {normalizedChannel} Outreach Studio
                  </div>
                )}

                <div className="cmp-studio-header-actions">
                  <button
                    type="button"
                    className={`cmp-studio-toggle-btn ${editorMode === 'edit' ? 'active' : ''}`}
                    onClick={() => setEditorMode('edit')}
                  >
                    <i className="fas fa-pencil-alt" /> Edit Template
                  </button>
                  <button
                    type="button"
                    className={`cmp-studio-toggle-btn ${editorMode === 'preview' ? 'active' : ''}`}
                    onClick={() => setEditorMode('preview')}
                  >
                    <i className="fas fa-eye" /> Live Preview
                  </button>
                </div>
              </div>

              {/* Studio Body Content */}
              <div className="cmp-studio-body">
                {isPhone ? (
                  /* Phone Battlecard Studio */
                  <div className="cmp-phone-battlecard-container">
                    <div className="cmp-outreach-form-group">
                      <label className="cmp-outreach-label">
                        <i className="fas fa-bolt" /> 10-Second Pattern-Interrupt Opener
                      </label>
                      {editorMode === 'edit' ? (
                        <textarea
                          className="cmp-outreach-textarea"
                          rows={2}
                          value={phoneBattlecard.opener}
                          onChange={(e) =>
                            setPhoneBattlecard((prev) => ({ ...prev, opener: e.target.value }))
                          }
                          onFocus={() => {
                            lastFocusedInputRef.current = 'opener';
                          }}
                          placeholder="Hi {{firstName}}, this is {{senderName}}..."
                        />
                      ) : (
                        <div className="cmp-preview-read-box">{previewPhoneBattlecard.opener}</div>
                      )}
                    </div>

                    <div className="cmp-outreach-form-group">
                      <label className="cmp-outreach-label">
                        <i className="fas fa-bullseye" /> Value Proposition &amp; Hook
                      </label>
                      {editorMode === 'edit' ? (
                        <textarea
                          className="cmp-outreach-textarea"
                          rows={2}
                          value={phoneBattlecard.valueProp}
                          onChange={(e) =>
                            setPhoneBattlecard((prev) => ({ ...prev, valueProp: e.target.value }))
                          }
                          onFocus={() => {
                            lastFocusedInputRef.current = 'valueProp';
                          }}
                          placeholder="We help companies like {{companyName}} solve..."
                        />
                      ) : (
                        <div className="cmp-preview-read-box">
                          {previewPhoneBattlecard.valueProp}
                        </div>
                      )}
                    </div>

                    {/* Discovery Questions */}
                    <div className="cmp-outreach-form-group">
                      <label className="cmp-outreach-label">
                        <i className="fas fa-question-circle" /> Discovery &amp; Qualification
                        Questions
                      </label>
                      <div className="cmp-phone-questions-list">
                        {previewPhoneBattlecard.qualifyingQuestions.map((q, qIdx) => (
                          <div key={qIdx} className="cmp-phone-question-item">
                            <span className="cmp-phone-q-num">{qIdx + 1}</span>
                            <p className="cmp-phone-q-text">
                              {typeof q === 'string'
                                ? q
                                : q?.question || q?.text || q?.name || String(q || '')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Objection Nav */}
                    <div className="cmp-outreach-form-group">
                      <label className="cmp-outreach-label">
                        <i className="fas fa-shield-alt" /> Objection Handling Playbook
                      </label>
                      <div className="cmp-objection-nav">
                        {[
                          { key: 'competitor', label: 'Competitor' },
                          { key: 'notInterested', label: 'Not Interested' },
                          { key: 'sendEmail', label: 'Send Email' },
                          { key: 'noBudget', label: 'No Budget' },
                          { key: 'badTiming', label: 'Bad Timing' },
                        ].map((tab) => (
                          <button
                            key={tab.key}
                            type="button"
                            className={`cmp-objection-tab ${activeObjectionKey === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveObjectionKey(tab.key)}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                      <div className="cmp-objection-box">
                        <div className="cmp-objection-text">
                          {previewPhoneBattlecard.objectionHandlers[activeObjectionKey] ||
                            'Select an objection to see the recommended rebuttal.'}
                        </div>
                      </div>
                    </div>

                    {/* Live Dialer Timer & Dispositions */}
                    <div className={`cmp-phone-timer-container ${callStatus}`}>
                      <div className="cmp-phone-timer-left">
                        <span className={`cmp-phone-status-dot ${callStatus}`} />
                        <span className="cmp-phone-status-label">
                          {callStatus === 'connected'
                            ? 'LIVE CALL IN PROGRESS'
                            : callStatus === 'calling'
                              ? 'DIALING OUT...'
                              : callStatus === 'ended'
                                ? 'CALL COMPLETED'
                                : 'READY TO CALL'}
                        </span>
                      </div>
                      <div className="cmp-phone-timer-right">
                        <span className="cmp-phone-timer-digits">{formatTimer(callSeconds)}</span>
                        {previewLead.phone && (
                          <button
                            type="button"
                            className="cmp-btn-quick-call"
                            onClick={() => handleInitiateCall(previewLead)}
                          >
                            <i className="fas fa-phone-alt" /> Call
                          </button>
                        )}
                        {(callStatus === 'connected' || callStatus === 'calling') && (
                          <button
                            type="button"
                            className="cmp-btn-end-call"
                            onClick={handleEndCall}
                          >
                            <i className="fas fa-phone-slash" /> End Call
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Call Outcome Dispositions */}
                    <div className="cmp-phone-disposition-section">
                      <label className="cmp-outreach-label">
                        <i className="fas fa-clipboard-check" /> Call Outcome Disposition
                      </label>
                      <div className="cmp-disposition-pills-grid">
                        {CALL_DISPOSITIONS.map((disp) => {
                          const isSelected = callDisposition === disp.id;
                          return (
                            <button
                              key={disp.id}
                              type="button"
                              className={`cmp-disposition-pill ${isSelected ? 'selected' : ''}`}
                              style={
                                isSelected
                                  ? {
                                      background: disp.color,
                                      borderColor: disp.color,
                                      color: '#ffffff',
                                    }
                                  : { borderColor: '#cbd5e1' }
                              }
                              onClick={() => setCallDisposition(disp.id)}
                            >
                              <i className={disp.icon} />
                              <span>{disp.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : isLinkedIn ? (
                  /* LinkedIn Studio */
                  <div className="cmp-linkedin-studio-container">
                    {linkedInMode === 'connection_note' ? (
                      <>
                        <div className="cmp-linkedin-subhead-row">
                          <label className="cmp-linkedin-subhead-label">Invitation Note</label>
                          <div className="cmp-char-progress-wrap">
                            <div className="cmp-char-progress-track">
                              <div
                                className={`cmp-char-progress-fill ${noteCharCount > 300 ? 'warn' : ''}`}
                                style={{ width: `${Math.min(100, (noteCharCount / 300) * 100)}%` }}
                              />
                            </div>
                            <span
                              className={`cmp-char-progress-text ${
                                noteCharCount > 300 ? 'warn' : ''
                              }`}
                            >
                              {noteCharCount} / 300 characters
                            </span>
                          </div>
                        </div>

                        {editorMode === 'edit' ? (
                          <div className="cmp-outreach-form-group">
                            <textarea
                              ref={noteTextareaRef}
                              className="cmp-outreach-textarea cmp-linkedin-note-textarea"
                              rows={5}
                              value={connectionNote}
                              onChange={(e) => {
                                setConnectionNote(e.target.value);
                                autoResizeTextarea(e.target, 130);
                              }}
                              onFocus={() => {
                                lastFocusedInputRef.current = 'note';
                              }}
                              placeholder="Personalized connection note with {{firstName}}, {{companyName}}..."
                            />
                          </div>
                        ) : (
                          /* Live Preview: LinkedIn Native Mockup Card */
                          <div className="cmp-linkedin-mockup-card">
                            <div className="cmp-linkedin-mockup-topbar">
                              <div className="cmp-linkedin-mockup-topbar-left">
                                <span className="cmp-linkedin-logo-badge">in</span>
                                <div className="cmp-linkedin-search-mockup">
                                  <i className="fas fa-search" />
                                  <span>Search LinkedIn</span>
                                </div>
                              </div>
                              <div className="cmp-linkedin-mockup-topbar-right">
                                <span className="cmp-linkedin-mockup-nav-item">
                                  <i className="fas fa-home" /> Home
                                </span>
                                <span className="cmp-linkedin-mockup-nav-item">
                                  <i className="fas fa-user-friends" /> Network
                                </span>
                                <span className="cmp-linkedin-mockup-nav-item">
                                  <i className="fas fa-comment-dots" /> Messaging
                                </span>
                              </div>
                            </div>

                            <div className="cmp-linkedin-mockup-banner" />

                            <div className="cmp-linkedin-mockup-profile">
                              <div className="cmp-linkedin-mockup-avatar">
                                {previewLead?.name ? previewLead.name.charAt(0).toUpperCase() : 'Y'}
                              </div>
                              <div className="cmp-linkedin-mockup-meta">
                                <div className="cmp-linkedin-mockup-name-row">
                                  <strong className="cmp-linkedin-mockup-name">
                                    {previewLead?.name || 'Prospect'}
                                  </strong>
                                  <span className="cmp-linkedin-mockup-degree"> &bull; 2nd</span>
                                </div>
                                <div className="cmp-linkedin-mockup-headline">
                                  {previewLead?.jobTitle || 'Executive'} at{' '}
                                  {previewLead?.company || 'Company'}
                                </div>
                                <div className="cmp-linkedin-mockup-subhead">
                                  {previewLead?.city || previewLead?.country || 'Malaysia'} &bull;{' '}
                                  500+ connections
                                </div>
                              </div>
                            </div>

                            <div className="cmp-linkedin-mockup-content-wrap">
                              <div className="cmp-linkedin-mockup-note-title">
                                <i className="far fa-envelope" /> Personalized Invitation Note
                                Preview:
                              </div>
                              <div className="cmp-linkedin-mockup-note-body">
                                {previewConnectionNote ? (
                                  previewConnectionNote.split('\n').map((para, i) => (
                                    <p key={i} className="cmp-linkedin-mockup-para">
                                      {para || <br />}
                                    </p>
                                  ))
                                ) : (
                                  <em>Synthesizing personalized connection note...</em>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* InMail Message Mode */
                      <>
                        <div className="cmp-outreach-form-group">
                          <label className="cmp-outreach-label">Subject Line</label>
                          {editorMode === 'edit' ? (
                            <input
                              ref={subjectInputRef}
                              type="text"
                              className="cmp-outreach-input cmp-subject-input"
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              onFocus={() => {
                                lastFocusedInputRef.current = 'subject';
                              }}
                              placeholder="InMail Subject..."
                            />
                          ) : (
                            <div className="cmp-preview-read-box subject">{previewSubject}</div>
                          )}
                        </div>

                        <div className="cmp-outreach-form-group">
                          <div className="cmp-outreach-label-row">
                            <label className="cmp-outreach-label">Email Body</label>
                            <span className="cmp-word-count-tag">{wordCount} words</span>
                          </div>
                          {editorMode === 'edit' ? (
                            <textarea
                              ref={bodyTextareaRef}
                              className="cmp-outreach-textarea cmp-body-textarea"
                              rows={6}
                              value={body}
                              onChange={(e) => {
                                setBody(e.target.value);
                                autoResizeTextarea(e.target, 150);
                              }}
                              onFocus={() => {
                                lastFocusedInputRef.current = 'body';
                              }}
                              placeholder="InMail body with {{firstName}}, {{companyName}}..."
                            />
                          ) : (
                            /* Live Preview: LinkedIn Native Mockup Card for InMail */
                            <div className="cmp-linkedin-mockup-card">
                              <div className="cmp-linkedin-mockup-topbar">
                                <div className="cmp-linkedin-mockup-topbar-left">
                                  <span className="cmp-linkedin-logo-badge">in</span>
                                  <div className="cmp-linkedin-search-mockup">
                                    <i className="fas fa-search" />
                                    <span>Search LinkedIn</span>
                                  </div>
                                </div>
                                <div className="cmp-linkedin-mockup-topbar-right">
                                  <span className="cmp-linkedin-mockup-nav-item">
                                    <i className="fas fa-home" /> Home
                                  </span>
                                  <span className="cmp-linkedin-mockup-nav-item">
                                    <i className="fas fa-user-friends" /> Network
                                  </span>
                                  <span className="cmp-linkedin-mockup-nav-item">
                                    <i className="fas fa-comment-dots" /> Messaging
                                  </span>
                                </div>
                              </div>

                              <div className="cmp-linkedin-mockup-banner" />

                              <div className="cmp-linkedin-mockup-profile">
                                <div className="cmp-linkedin-mockup-avatar">
                                  {previewLead?.name
                                    ? previewLead.name.charAt(0).toUpperCase()
                                    : 'Y'}
                                </div>
                                <div className="cmp-linkedin-mockup-meta">
                                  <div className="cmp-linkedin-mockup-name-row">
                                    <strong className="cmp-linkedin-mockup-name">
                                      {previewLead?.name || 'Prospect'}
                                    </strong>
                                    <span className="cmp-linkedin-mockup-degree"> &bull; 2nd</span>
                                  </div>
                                  <div className="cmp-linkedin-mockup-headline">
                                    {previewLead?.jobTitle || 'Executive'} at{' '}
                                    {previewLead?.company || 'Company'}
                                  </div>
                                  <div className="cmp-linkedin-mockup-subhead">
                                    {previewLead?.city || previewLead?.country || 'Malaysia'} &bull;{' '}
                                    500+ connections
                                  </div>
                                </div>
                              </div>

                              <div className="cmp-linkedin-mockup-content-wrap">
                                <div className="cmp-linkedin-mockup-note-title">
                                  <i className="far fa-envelope" /> Personalized Invitation Note
                                  Preview:
                                </div>
                                <div className="cmp-linkedin-mockup-note-body">
                                  {previewBody ? (
                                    previewBody.split('\n').map((para, i) => (
                                      <p key={i} className="cmp-linkedin-mockup-para">
                                        {para || <br />}
                                      </p>
                                    ))
                                  ) : (
                                    <em>Synthesizing InMail message...</em>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : isWhatsApp ? (
                  /* WhatsApp Studio */
                  <div className="cmp-whatsapp-studio-container">
                    <div className="cmp-outreach-form-group">
                      <div className="cmp-outreach-label-row">
                        <label className="cmp-outreach-label">WhatsApp Message Body</label>
                        <span className="cmp-word-count-tag">{wordCount} words</span>
                      </div>
                      {editorMode === 'edit' ? (
                        <textarea
                          ref={bodyTextareaRef}
                          className="cmp-outreach-textarea cmp-body-textarea"
                          rows={6}
                          value={body}
                          onChange={(e) => {
                            setBody(e.target.value);
                            autoResizeTextarea(e.target, 150);
                          }}
                          onFocus={() => {
                            lastFocusedInputRef.current = 'body';
                          }}
                          placeholder="Personalized WhatsApp message..."
                        />
                      ) : (
                        /* Live Preview: WhatsApp Chat Mockup Card */
                        <div className="cmp-whatsapp-mockup-card">
                          <div className="cmp-whatsapp-mockup-topbar">
                            <div className="cmp-whatsapp-mockup-topbar-left">
                              <div className="cmp-whatsapp-mockup-avatar">
                                {previewLead?.name ? previewLead.name.charAt(0).toUpperCase() : 'W'}
                              </div>
                              <div className="cmp-whatsapp-mockup-meta">
                                <span className="cmp-whatsapp-mockup-name">
                                  {previewLead?.name || 'Prospect'}
                                </span>
                                <span className="cmp-whatsapp-mockup-status">
                                  {previewLead?.phone || '+60 12-345 6789'} &bull; online
                                </span>
                              </div>
                            </div>
                            <div className="cmp-whatsapp-mockup-topbar-right">
                              <span className="cmp-whatsapp-mockup-icon" title="Video Call">
                                <i className="fas fa-video" />
                              </span>
                              <span className="cmp-whatsapp-mockup-icon" title="Voice Call">
                                <i className="fas fa-phone-alt" />
                              </span>
                              <span className="cmp-whatsapp-mockup-icon" title="More Options">
                                <i className="fas fa-ellipsis-v" />
                              </span>
                            </div>
                          </div>

                          <div className="cmp-whatsapp-mockup-chat">
                            <div className="cmp-whatsapp-date-pill">TODAY</div>

                            <div className="cmp-whatsapp-bubble outgoing">
                              <div className="cmp-whatsapp-bubble-content">
                                {previewBody ? (
                                  previewBody.split('\n').map((para, i) => (
                                    <p key={i} className="cmp-whatsapp-msg-para">
                                      {para || <br />}
                                    </p>
                                  ))
                                ) : (
                                  <em>Synthesizing personalized WhatsApp message...</em>
                                )}
                              </div>
                              <div className="cmp-whatsapp-msg-meta">
                                <span className="cmp-whatsapp-msg-time">
                                  {new Date().toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                                <i
                                  className="fas fa-check-double cmp-whatsapp-ticks"
                                  title="Read"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="cmp-whatsapp-mockup-inputbar">
                            <span className="cmp-whatsapp-input-icon">
                              <i className="far fa-smile" />
                            </span>
                            <span className="cmp-whatsapp-input-icon">
                              <i className="fas fa-paperclip" />
                            </span>
                            <div className="cmp-whatsapp-mockup-input">Type a message</div>
                            <span className="cmp-whatsapp-input-icon mic">
                              <i className="fas fa-microphone" />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Email Studio */
                  <div className="cmp-email-studio-container">
                    {editorMode === 'edit' ? (
                      <>
                        {/* Subject Line */}
                        <div className="cmp-outreach-form-group">
                          <label className="cmp-outreach-label">Subject Line</label>
                          <input
                            ref={subjectInputRef}
                            type="text"
                            className="cmp-outreach-input cmp-subject-input"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            onFocus={() => {
                              lastFocusedInputRef.current = 'subject';
                            }}
                            placeholder="Strategic pipeline insights for {{companyName}}"
                          />
                        </div>

                        {/* Email Body */}
                        <div className="cmp-outreach-form-group">
                          <div className="cmp-outreach-label-row">
                            <label className="cmp-outreach-label">Email Body</label>
                            <span className="cmp-word-count-tag">{wordCount} words</span>
                          </div>
                          <textarea
                            ref={bodyTextareaRef}
                            className="cmp-outreach-textarea cmp-body-textarea"
                            rows={6}
                            value={body}
                            onChange={(e) => {
                              setBody(e.target.value);
                              autoResizeTextarea(e.target, 150);
                            }}
                            onFocus={() => {
                              lastFocusedInputRef.current = 'body';
                            }}
                            placeholder="Hi {{firstName}},&#10;&#10;Reaching out because many executives at companies like {{companyName}} are currently tackling outbound predictability and data freshness.&#10;&#10;Our platform enables teams to 3.5x qualified meetings with verified direct dials.&#10;&#10;Are you available for a 15-minute introductory call this Thursday?"
                          />
                        </div>
                      </>
                    ) : (
                      /* Live Preview: Email Client Mockup Card */
                      <div className="cmp-email-mockup-card">
                        <div className="cmp-email-mockup-headers">
                          <div className="cmp-email-header-row">
                            <span className="cmp-email-header-tag">To</span>
                            <div className="cmp-email-recipient-chip">
                              <span className="cmp-email-recipient-avatar">
                                {previewLead?.name ? previewLead.name.charAt(0).toUpperCase() : 'P'}
                              </span>
                              <span className="cmp-email-recipient-name">
                                {previewLead?.name || 'Prospect'}
                              </span>
                              <span className="cmp-email-recipient-addr">
                                &lt;{previewLead?.email || 'prospect@company.com'}&gt;
                              </span>
                            </div>
                          </div>
                          <div className="cmp-email-header-row subject-row">
                            <span className="cmp-email-header-tag">Subject</span>
                            <span className="cmp-email-subject-text">
                              {previewSubject || '(No subject)'}
                            </span>
                          </div>
                        </div>

                        <div className="cmp-email-mockup-content">
                          <div className="cmp-email-sender-meta">
                            <div className="cmp-email-sender-avatar">
                              {effectiveSenderName
                                ? effectiveSenderName.charAt(0).toUpperCase()
                                : 'S'}
                            </div>
                            <div className="cmp-email-sender-info">
                              <div className="cmp-email-sender-name-line">
                                <strong>{effectiveSenderName}</strong>
                                <span className="cmp-email-sender-email">
                                  &lt;{effectiveSenderEmail}&gt;
                                </span>
                              </div>
                              <span className="cmp-email-sent-time">
                                to {previewLead?.name || 'Prospect'} &bull; Just now
                              </span>
                            </div>
                            <div className="cmp-email-meta-actions">
                              <i className="far fa-star" title="Star" />
                              <i className="fas fa-reply" title="Reply" />
                            </div>
                          </div>

                          <div className="cmp-email-msg-body">
                            {previewBody ? (
                              previewBody.split('\n').map((para, i) => (
                                <p key={i} className="cmp-email-para">
                                  {para || <br />}
                                </p>
                              ))
                            ) : (
                              <em>Generating email draft...</em>
                            )}
                          </div>
                        </div>

                        <div className="cmp-email-mockup-toolbar">
                          <div className="cmp-email-toolbar-left">
                            <div className="cmp-email-send-mock-btn">
                              <span>Send</span>
                              <i className="fas fa-caret-down" />
                            </div>
                            <div className="cmp-email-format-icons">
                              <span
                                className="cmp-email-tool-icon active"
                                title="Formatting Options"
                              >
                                <i className="fas fa-font" />
                              </span>
                              <span className="cmp-email-tool-icon" title="Attach Files">
                                <i className="fas fa-paperclip" />
                              </span>
                              <span className="cmp-email-tool-icon" title="Insert Link">
                                <i className="fas fa-link" />
                              </span>
                              <span className="cmp-email-tool-icon" title="Insert Emoji">
                                <i className="far fa-smile" />
                              </span>
                              <span className="cmp-email-tool-icon" title="Insert Files from Drive">
                                <i className="fab fa-google-drive" />
                              </span>
                              <span className="cmp-email-tool-icon" title="Insert Photo">
                                <i className="far fa-image" />
                              </span>
                              <span className="cmp-email-tool-icon" title="Confidential Mode">
                                <i className="fas fa-lock" />
                              </span>
                            </div>
                          </div>
                          <div className="cmp-email-toolbar-right">
                            <span className="cmp-email-tool-icon trash" title="Discard Draft">
                              <i className="far fa-trash-alt" />
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick AI Action Toolbar inside studio */}
                <div className="cmp-studio-bottom-actions">
                  {!hasGeneratedAi ? (
                    <button
                      type="button"
                      className="cmp-btn-studio-action generate-ai"
                      onClick={handleGenerateAiContent}
                      disabled={isGenerating}
                    >
                      <i className={`fas fa-magic ${isGenerating ? 'fa-spin' : ''}`} />
                      {isGenerating ? 'Generating...' : 'Generate with AI'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="cmp-btn-studio-action regen"
                      onClick={handleGenerateAiContent}
                      disabled={isGenerating}
                    >
                      <i className={`fas fa-magic ${isGenerating ? 'fa-spin' : ''}`} />
                      {isGenerating ? 'Regenerating...' : 'Regenerate'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Modal Footer ── */}
        <div className="cmp-outreach-modal-footer">
          <div className="cmp-outreach-footer-left">
            <span className="cmp-footer-contacted-status">
              <i className="fas fa-check-circle cmp-status-check-icon" /> {dispatchedLeadIds.size}{' '}
              of {channelRecipients.length} Prospects Contacted
            </span>
          </div>

          <div className="cmp-outreach-footer-right">
            <button type="button" className="cmp-btn-footer-secondary" onClick={handleModalClose}>
              Finish &amp; Close
            </button>

            {isPhone ? (
              <button
                type="button"
                className="cmp-btn-footer-primary phone"
                disabled={!callDisposition}
                onClick={() => handleLogCallOutcome(previewLead)}
                title="Log call outcome to activity history and advance to next lead"
              >
                <i className="fas fa-clipboard-check" />{' '}
                {selectedRecipientId === 'ALL' && channelRecipients.length > 1
                  ? `Log Outcome & Next (${activeQueueIndex + 1}/${channelRecipients.length}: ${(previewLead.name || 'Lead').split(' ')[0]})`
                  : `Log Call Outcome (${(previewLead.name || 'Lead').split(' ')[0]})`}
              </button>
            ) : isLinkedIn ? (
              selectedRecipientId === 'ALL' ? (
                <>
                  <button
                    type="button"
                    className={`cmp-btn-footer-secondary ${
                      copiedButtonId === 'footer-copy-open' ? 'cmp-btn-copied' : ''
                    }`}
                    onClick={() => handleCopyAndOpenLinkedIn(null, 'footer-copy-open')}
                  >
                    <i
                      className={
                        copiedButtonId === 'footer-copy-open' ? 'fas fa-check' : 'fas fa-copy'
                      }
                    />{' '}
                    {copiedButtonId === 'footer-copy-open'
                      ? 'Copied!'
                      : linkedInMode === 'inmail'
                        ? 'Copy InMail & Open LinkedIn'
                        : 'Copy Note & Open LinkedIn'}
                  </button>
                  <button
                    type="button"
                    className="cmp-btn-footer-primary linkedin"
                    onClick={() => handleNextLinkedInLead(null, 'footer-copy-next')}
                  >
                    <span className="cmp-btn-in-badge">in</span>{' '}
                    {linkedInMode === 'inmail'
                      ? 'Copy InMail, Open & Next'
                      : 'Copy Note, Open & Next'}{' '}
                    (
                    {channelRecipients.length > 0
                      ? `${activeQueueIndex + 1}/${channelRecipients.length}: ${currentLinkedInQueueLead?.name || previewLead.name || 'Lead'}`
                      : 'None'}
                    )
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="cmp-btn-footer-primary linkedin"
                  onClick={() => handleCopyAndOpenLinkedIn(activeRecipient, 'single-copy-open')}
                >
                  <span className="cmp-btn-in-badge">in</span>{' '}
                  {linkedInMode === 'inmail'
                    ? 'Copy InMail & Open LinkedIn'
                    : 'Copy Note & Open LinkedIn'}{' '}
                  ({previewLead.name || 'Lead'})
                </button>
              )
            ) : isWhatsApp ? (
              <a
                href={footerWaUrl || '#'}
                target={footerWaUrl ? '_blank' : undefined}
                rel={footerWaUrl ? 'noopener noreferrer' : undefined}
                className={`cmp-btn-footer-primary whatsapp ${!footerWaUrl ? 'disabled' : ''}`}
                onClick={(e) => {
                  if (!footerWaUrl || !footerWaLead) {
                    e.preventDefault();
                    alert(
                      footerWaLead?.name
                        ? `No phone number found for ${footerWaLead.name}.`
                        : 'Please select a recipient with a valid phone number.'
                    );
                    return;
                  }
                  recordWhatsAppDispatch(footerWaLead);
                  if (selectedRecipientId === 'ALL' && channelRecipients.length > 1) {
                    const nextIdx = (activeQueueIndex + 1) % channelRecipients.length;
                    setTimeout(() => {
                      setActiveQueueIndex(nextIdx);
                    }, 150);
                  }
                }}
              >
                <i className="fab fa-whatsapp" />{' '}
                {selectedRecipientId !== 'ALL' ? (
                  `Send WhatsApp (${footerWaLead?.name || 'Lead'})`
                ) : (
                  <>
                    Send WhatsApp &amp; Next (
                    {channelRecipients.length > 0
                      ? `${activeQueueIndex + 1}/${channelRecipients.length}: ${(footerWaLead?.name || 'Lead').split(' ')[0]}`
                      : 'None'}
                    )
                  </>
                )}
              </a>
            ) : (
              /* Email Footer Buttons */
              <>
                {selectedRecipientId !== 'ALL' ? (
                  <>
                    <button
                      type="button"
                      className="cmp-btn-footer-secondary cmp-btn-gmail"
                      onClick={() => handleNextGmailLead(activeRecipient)}
                      disabled={!activeRecipient?.email}
                      title={`Open personalized draft in Gmail Web for ${previewLead?.name || 'Lead'}`}
                    >
                      <i className="fab fa-google" /> Open in Gmail ({previewLead?.name || 'Lead'})
                    </button>
                    <button
                      type="button"
                      className="cmp-btn-footer-primary"
                      onClick={() => handleSendBatchPlatform(activeRecipient)}
                      disabled={isSendingViaPlatform || !activeRecipient?.email}
                    >
                      <i
                        className={
                          isSendingViaPlatform ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'
                        }
                      />{' '}
                      {isSendingViaPlatform
                        ? 'Sending...'
                        : `Send to ${previewLead?.name || 'Lead'}`}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="cmp-btn-footer-secondary cmp-btn-gmail"
                      onClick={() => handleNextGmailLead(null)}
                      disabled={channelRecipients.length === 0}
                      title={`Open personalized draft in Gmail Web for ${currentQueueLead?.name || 'lead'} and advance to next`}
                    >
                      <i className="fab fa-google" /> Open in Gmail &amp; Next (
                      {channelRecipients.length > 0
                        ? `${currentLeadDisplayIndex}/${channelRecipients.length}: ${currentQueueLead?.name || previewLead.name || 'Lead'}`
                        : 'None'}
                      )
                    </button>
                    <button
                      type="button"
                      className="cmp-btn-footer-primary"
                      onClick={() => handleSendBatchPlatform(null)}
                      disabled={isSendingViaPlatform || channelRecipients.length === 0}
                    >
                      <i
                        className={isSendingViaPlatform ? 'fas fa-spinner fa-spin' : 'fas fa-bolt'}
                      />{' '}
                      {isSendingViaPlatform
                        ? 'Sending...'
                        : `Send to All (${channelRecipients.length})`}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
